import { getServicesBaseUrl } from "../../helpers/apiEnv"
import React, { useEffect, useState, useCallback } from "react";
import { connect } from "react-redux";
import { setBreadcrumbItems } from "../../store/actions";
import {
	Row,
	Col,
	Card,
	CardHeader,
	CardBody,
	Table,
	Input,
	Button,
	Nav,
	NavItem,
	NavLink,
	TabContent,
	TabPane,
	Badge,
	Alert,
	Label,
	FormGroup,
	UncontrolledTooltip,
	Spinner,
} from "reactstrap";
import axios from "axios";
import { Line, Doughnut } from "react-chartjs-2";
import * as XLSX from "xlsx";
import {
	Chart as ChartJS,
	CategoryScale,
	LinearScale,
	PointElement,
	LineElement,
	Title,
	Tooltip,
	Legend,
	ArcElement,
} from "chart.js";
import "./DashboardV2.css";

ChartJS.register(
	CategoryScale,
	LinearScale,
	PointElement,
	LineElement,
	Title,
	Tooltip,
	Legend,
	ArcElement
);

const baseURL = process.env.REACT_APP_API_URL;
const newAPIURL = getServicesBaseUrl().replace(/\/+$/, ""); // dinámico: respeta Prod/QA

//const newAPIURL = "http://localhost:3100/a2/api/v1";
//const newAPIURL = "https://e80f7406-be1c-41a5-a1f5-6565ed688d21-00-3jfszfoz4s9a1.picard.replit.dev/a1/api/v1";

// ──────────────────────────────────────────────────────────────────
// Helpers para renderizar variaciones del back de forma segura.
// El back devuelve `variacion` y `variacion_total` en 2 formatos:
//   - string viejo: "+12.3%" / "-5.1%"
//   - objeto nuevo: { facturacion_neto_pct, facturacion_neto_abs,
//                     documentos_pct, color }
// Estos helpers extraen un texto y un color renderizables sin reventar
// con React error #31 (Objects are not valid as a React child).
// ──────────────────────────────────────────────────────────────────
const variacionToText = (v) => {
	if (v == null) return "—"
	if (typeof v === "string" || typeof v === "number") return String(v)
	if (typeof v === "object") {
		return (
			v.facturacion_neto_pct
			|| v.variacion
			|| v.label
			|| v.text
			|| (v.facturacion_neto_abs != null ? String(v.facturacion_neto_abs) : "—")
		)
	}
	return "—"
}

const variacionToColor = (v) => {
	if (v == null) return "success"
	if (typeof v === "string") return v.includes("-") ? "danger" : "success"
	if (typeof v === "object") {
		// El back puede mandar `color: "success" | "danger" | "warning" | "info"`
		// o términos crudos. Normalizamos.
		const c = String(v.color || "").toLowerCase()
		if (["danger", "error", "red"].includes(c)) return "danger"
		if (["success", "ok", "green"].includes(c)) return "success"
		if (["warning", "warn", "yellow", "orange"].includes(c)) return "warning"
		if (["info", "blue"].includes(c)) return "info"
		// Sin pista explícita, deducir del texto
		const txt = variacionToText(v)
		return String(txt).includes("-") ? "danger" : "success"
	}
	return "success"
}

// ──────────────────────────────────────────────────────────────────
// Adapter del Dashboard
// ──────────────────────────────────────────────────────────────────
// El back de /analytics/dashboard cambió de shape:
//   antes:  { resumen_ejecutivo: {...}, tendencias_mensuales, top_sucursales[],
//             top_clientes[], periodo (string), fecha_inicio, fecha_fin }
//   ahora:  { kpis_principales, comparativo_year_anterior, distribucion_por_tipo,
//             tendencia_mensual, top_sucursales[] (otro schema),
//             top_clientes[] (otro schema), top_productos, tributario,
//             concentracion, alertas, insights, periodo (objeto),
//             meta { nombre_emisor }, ... }
//
// En vez de parchar los 40 lugares donde el JSX lee los campos, mapeamos
// la respuesta nueva al formato viejo. Si el back vuelve a cambiar, sólo
// hay que tocar este adapter.
const adaptDashboardData = (raw) => {
	if (!raw) return null
	// Si la respuesta ya viene en formato viejo (tiene resumen_ejecutivo),
	// devolverla tal cual — backward compatible.
	if (raw.resumen_ejecutivo) return raw

	const kpis = raw.kpis_principales || {}
	const compYear = raw.comparativo_year_anterior || raw.comparativo_periodo_anterior || {}
	const periodo = raw.periodo || {}

	const fmtPct = (n) => {
		if (n == null || isNaN(Number(n))) return null
		const v = Number(n)
		return `${v > 0 ? "+" : ""}${v.toFixed(2)}%`
	}

	return {
		// Conservamos todos los campos nuevos por si alguna sección los usa directo
		...raw,
		// Campos top-level que el render legacy espera
		nombreemisor: raw.nombre_emisor || raw.meta?.nombre_emisor,
		fecha_inicio: periodo.fecha_inicio,
		fecha_fin: periodo.fecha_fin,
		// periodo se mantiene como objeto — variacionToText helper lo maneja
		resumen_ejecutivo: {
			facturacion_total: kpis.facturacion_neta,
			total_documentos: kpis.documentos,
			ticket_promedio: kpis.ticket_promedio,
			subtotal_total: kpis.base_gravable,
			sucursales_activas: kpis.sucursales_activas,
			impuesto_total: kpis.iva_total,
			crecimiento_vs_anterior: fmtPct(compYear.facturacion_neta_pct),
			por_tipo_documento: (raw.distribucion_por_tipo || []).map(t => ({
				tipo_comprobante: t.codigo,
				efecto_contable: (t.monto_neto || 0) >= 0 ? "SUMA" : "RESTA",
				total_documentos: t.documentos || 0,
				facturacion_total: t.monto_neto || 0,
				subtotal_total: t.monto_neto || 0,
				impuesto_total: 0,
				participacion: t.porcentaje_monto != null
					? `${Number(t.porcentaje_monto).toFixed(2)}%`
					: "0%",
				ticket_promedio: (t.documentos || 0) > 0
					? (t.monto_neto || 0) / t.documentos
					: 0,
				tipo_documento: t.tipo_documento,
				categoria: t.categoria,
			})),
		},
		tendencias_mensuales: (raw.tendencia_mensual || []).map(m => ({
			mes: m.mes,
			nombre_mes: m.nombre_mes,
			total_ventas: m.monto_neto || 0,
			total_lineas: m.documentos || 0,
			iva: m.iva || 0,
			crecimiento_vs_mes_anterior_pct: m.crecimiento_vs_mes_anterior_pct,
		})),
		top_sucursales: (raw.top_sucursales || []).map(s => ({
			sucursal: s.sucursal,
			storeid: s.store_id || s.storeid || "",
			facturacion: s.monto_neto || 0,
			documentos: s.documentos || 0,
			participacion: s.porcentaje != null
				? `${Number(s.porcentaje).toFixed(2)}%`
				: "0%",
			ticket_promedio: s.ticket_promedio || 0,
		})),
		top_clientes: (raw.top_clientes || []).map(c => ({
			cliente: c.cliente,
			nit_receptor: c.nit_receptor,
			facturacion: c.monto_neto || 0,
			documentos: c.documentos || 0,
			participacion: c.porcentaje != null
				? `${Number(c.porcentaje).toFixed(2)}%`
				: "0%",
			ticket_promedio: c.ticket_promedio || 0,
		})),
	}
}



// Helper: fecha local como YYYY-MM-DD (evita desfase UTC)
const toLocalDateString = (date) => {
	if (!date) return null;
	const y = date.getFullYear();
	const m = String(date.getMonth() + 1).padStart(2, "0");
	const d = String(date.getDate()).padStart(2, "0");
	return `${y}-${m}-${d}`;
};

const Dashboard = ({ setBreadcrumbItems, user, empresas, empresaSeleccionada }) => {
	document.title = "LS-DA / Softlogy";
	const userGreeting = `Bienvenido, ${user ? `${user.nombre} ${user.apellido}` : "Usuario"}`;
	const currentYear = Math.max(2026, new Date().getFullYear());

	// Estados para Dashboard Ejecutivo
	const [activeTab, setActiveTab] = useState("1");
	const [dashboardData, setDashboardData] = useState(null);
	const [sucursalesData, setSucursalesData] = useState(null);
	const [comparativoDiarioData, setComparativoDiarioData] = useState(null);
	const [documentosGeneradosData, setDocumentosGeneradosData] = useState(null);
	const [selectedYear, setSelectedYear] = useState(currentYear);
	const [selectedMonth, setSelectedMonth] = useState(new Date().getMonth() + 1);

	// Estados para modo de consulta (año o rango de fechas)
	const [consultaMode, setConsultaMode] = useState("año"); // "año" o "rango"
	const hoy = toLocalDateString(new Date()); // YYYY-MM-DD
	const [fechaInicio, setFechaInicio] = useState(hoy);
	const [fechaFin, setFechaFin] = useState(hoy);

	// Estados de loading y error
	const [isLoadingDashboard, setIsLoadingDashboard] = useState(false);
	const [isLoadingSucursales, setIsLoadingSucursales] = useState(false);
	const [isLoadingComparativo, setIsLoadingComparativo] = useState(false);
	const [isLoadingDocumentosGenerados, setIsLoadingDocumentosGenerados] = useState(false);
	const [indicadoresGeneralesData, setIndicadoresGeneralesData] = useState(null);
	const [isLoadingIndicadores, setIsLoadingIndicadores] = useState(false);
	const [error, setError] = useState(null);

	// Estados para Análisis Facturadores
	const [facturasPersonalizadasData, setFacturasPersonalizadasData] = useState(null);
	const [isLoadingFacturasPersonalizadas, setIsLoadingFacturasPersonalizadas] = useState(false);
	const [facturasInicio, setFacturasInicio] = useState("");
	const [facturasFin, setFacturasFin] = useState("");

	// Formateador de moneda
	const formatoCOP = new Intl.NumberFormat("es-CO", {
		style: "currency",
		currency: "COP",
		minimumFractionDigits: 2,
	});

	// Formateador de números
	const formatoNumero = new Intl.NumberFormat("es-CO");

	// Ajustes puntuales de etiquetas
	const getDescripcionTipo = (tipo) => {
		if (tipo?.tipo_comprobante === "92" || tipo?.tipo_comprobante === 92) {
			return "Nota de Debito";
		}
		return tipo?.descripcion_tipo;
	};

	// Breadcrumb
	useEffect(() => {
		if (user) {
			const breadcrumbItems = [
				{ title: "LS-DA", link: "#" },
				{ title: "Dashboard", link: "#" },
			];
			setBreadcrumbItems(userGreeting, breadcrumbItems);
		}
	}, [setBreadcrumbItems, user, userGreeting]);

	// Empresas y empresa seleccionada vienen de Redux (props)

	// Manejar cambio de modo de consulta
	const handleConsultaModeChange = (mode) => {
		setConsultaMode(mode);
		setDashboardData(null);
		setSucursalesData(null);
		setComparativoDiarioData(null);

		// Si cambia a modo año, limpiar fechas
		if (mode === "año") {
			setFechaInicio("");
			setFechaFin("");
		}
		// Si cambia a modo rango, mantener año actual como referencia
		else if (mode === "rango") {
		}
	};

	// Manejar cambio de fecha inicio
	const handleFechaInicioChange = (e) => {
		const newFecha = e.target.value;
		setFechaInicio(newFecha);
	};

	// Manejar cambio de fecha fin
	const handleFechaFinChange = (e) => {
		const newFecha = e.target.value;
		setFechaFin(newFecha);
	};

	// Validar que los campos requeridos estén completos según el modo
	const isFormValid = () => {
		if (!empresaSeleccionada) return false;

		if (consultaMode === "año") {
			const valid = selectedYear;
			return valid;
		} else {
			const valid = fechaInicio && fechaFin && fechaInicio <= fechaFin;
			return valid;
		}
	};

	// Funciones de descarga Excel (preparadas para uso futuro)
	// const downloadClientesExcel = () => {
	//   if (!clientesPorEmpresa.length) return
	//   const data = [
	//     ...clientesPorEmpresa[0].topClientes.map(item => ({
	//       Cliente: item.nombre,
	//       Importe: item.importe,
	//     })),
	//     { Cliente: "TOTAL", Importe: clientesPorEmpresa[0].totalClientes },
	//   ]
	//   downloadExcel(data, "clientes", "Clientes")
	// }

	// const downloadSucursalesExcel = () => {
	//   if (!sucursales.length) return
	//   const data = [
	//     ...sucursales.map(s => ({
	//       Sucursal: s.nombreSucursal,
	//       Centro: s.storeid,
	//       Importe: s.totalImporte,
	//       Documentos: s.totalDocumentos,
	//     })),
	//     {
	//       Sucursal: "TOTAL",
	//       Centro: "",
	//       Importe: sucursales.reduce((acc, s) => acc + s.totalImporte, 0),
	//       Documentos: sucursales.reduce((acc, s) => acc + s.totalDocumentos, 0),
	//     },
	//   ]
	//   downloadExcel(data, "sucursales", "Sucursales")
	// }

	// Funciones para Dashboard Ejecutivo
	const fetchDashboardData = useCallback(async () => {
		if (!empresaSeleccionada) return;

		const nit = empresaSeleccionada.nit;

		setIsLoadingDashboard(true);
		try {
			let url = `${newAPIURL}/analytics/dashboard?nitemisor=${encodeURIComponent(nit)}`;

			if (consultaMode === "año") {
				url += `&year=${selectedYear}`;
			} else {
				url += `&fecha_inicio=${fechaInicio}&fecha_fin=${fechaFin}`;
			}

			const response = await axios.get(url);

			setDashboardData(adaptDashboardData(response.data));
		} catch (error) {
			setError("Error al cargar los datos del dashboard");
		} finally {
			setIsLoadingDashboard(false);
		}
	}, [empresaSeleccionada, selectedYear, consultaMode, fechaInicio, fechaFin]);

	const fetchSucursalesData = useCallback(async () => {
		if (!empresaSeleccionada) return;

		const nit = empresaSeleccionada.nit;

		setIsLoadingSucursales(true);
		try {
			let url = `${newAPIURL}/analytics/sucursales?nitemisor=${encodeURIComponent(nit)}`;

			if (consultaMode === "año") {
				url += `&year=${selectedYear}`;
			} else {
				url += `&fecha_inicio=${fechaInicio}&fecha_fin=${fechaFin}`;
			}

			const response = await axios.get(url);
			setSucursalesData(response.data);
		} catch (error) {
			setSucursalesData(null);
		} finally {
			setIsLoadingSucursales(false);
		}
	}, [empresaSeleccionada, selectedYear, consultaMode, fechaInicio, fechaFin]);

	const fetchComparativoDiarioData = useCallback(async () => {
		if (!empresaSeleccionada) return;

		const nit = empresaSeleccionada.nit;

		setIsLoadingComparativo(true);
		try {
			let url = `${newAPIURL}/analytics/comparativo-diario?nitemisor=${encodeURIComponent(nit)}`;

			if (consultaMode === "año") {
				url += `&year=${selectedYear}`;
			} else {
				url += `&fecha_inicio=${fechaInicio}&fecha_fin=${fechaFin}`;
			}

			const response = await axios.get(url);
			setComparativoDiarioData(response.data);
		} catch (error) {
			setComparativoDiarioData(null);
		} finally {
			setIsLoadingComparativo(false);
		}
	}, [empresaSeleccionada, selectedYear, consultaMode, fechaInicio, fechaFin]);

	const fetchDocumentosGeneradosData = useCallback(async () => {
		if (!empresaSeleccionada) return;

		const nit = empresaSeleccionada.nit;

		setIsLoadingDocumentosGenerados(true);
		try {
			const url = `${newAPIURL}/analytics/documentos-generados?nitemisor=${encodeURIComponent(nit)}&year=${selectedYear}&mes=${selectedMonth}`;
			const response = await axios.get(url);
			setDocumentosGeneradosData(response.data);
		} catch (error) {
			setDocumentosGeneradosData(null);
		} finally {
			setIsLoadingDocumentosGenerados(false);
		}
	}, [empresaSeleccionada, selectedYear, selectedMonth]);

	// Función para obtener indicadores generales
	const fetchIndicadoresGeneralesData = useCallback(async () => {
		if (!empresaSeleccionada || !selectedMonth) return;

		const nit = empresaSeleccionada.nit;

		setIsLoadingIndicadores(true);
		try {
			const url = `${newAPIURL}/analytics/indicadores-generales?nitemisor=${encodeURIComponent(nit)}&year=${selectedYear}&mes=${selectedMonth}`;
			const response = await axios.get(url);
			setIndicadoresGeneralesData(response.data);
		} catch (error) {
			setIndicadoresGeneralesData(null);
		} finally {
			setIsLoadingIndicadores(false);
		}
	}, [empresaSeleccionada, selectedYear, selectedMonth]);

	// Fetch para Análisis Facturadores
	const fetchFacturasPersonalizadas = useCallback(async () => {
		if (!empresaSeleccionada || !facturasInicio || !facturasFin) return;

		const nit = empresaSeleccionada.nit;

		setIsLoadingFacturasPersonalizadas(true);
		try {
			const url = `${newAPIURL}/analytics/facturas-personalizadas?nitemisor=${encodeURIComponent(nit)}&fecha_inicio=${facturasInicio}&fecha_fin=${facturasFin}`;
			const response = await axios.get(url);
			setFacturasPersonalizadasData(response.data);
		} catch (err) {
			setFacturasPersonalizadasData(null);
		} finally {
			setIsLoadingFacturasPersonalizadas(false);
		}
	}, [empresaSeleccionada, facturasInicio, facturasFin]);

	// Export Excel para Análisis Facturadores
	const handleExportFacturasExcel = () => {
		if (!facturasPersonalizadasData) return;

		const wb = XLSX.utils.book_new();

		// ── Hoja 1: Resumen ──────────────────────────────────────────────────
		const resumenData = [
			["ANÁLISIS DE FACTURADORES"],
			[],
			["Empresa:", facturasPersonalizadasData.nombre_emisor || ""],
			["NIT Emisor:", facturasPersonalizadasData.nitemisor || ""],
			["Período:", `${facturasPersonalizadasData.fecha_inicio} al ${facturasPersonalizadasData.fecha_fin}`],
			["Fecha de Exportación:", new Date().toLocaleDateString("es-CO")],
			[],
			["INDICADORES GENERALES"],
			[],
			["Indicador", "Cantidad / Valor"],
			["Total Facturas", facturasPersonalizadasData.total_facturas || 0],
			["Total Personalizadas", facturasPersonalizadasData.total_personalizadas || 0],
			["Total Genéricas", facturasPersonalizadasData.total_genericas || 0],
			["% Personalizadas", `${(facturasPersonalizadasData.porcentaje_personalizadas || 0).toFixed(2)}%`],
			[],
			["VALORES ECONÓMICOS"],
			[],
			["Concepto", "Subtotal", "Impuestos", "Total"],
			["Personalizadas",
				facturasPersonalizadasData.subtotal_personalizadas || 0,
				facturasPersonalizadasData.impuestos_personalizadas || 0,
				facturasPersonalizadasData.total_personalizadas_valor || 0],
			["Genéricas",
				facturasPersonalizadasData.subtotal_genericas || 0,
				facturasPersonalizadasData.impuestos_genericas || 0,
				facturasPersonalizadasData.total_genericas_valor || 0],
		];

		const wsResumen = XLSX.utils.aoa_to_sheet(resumenData);
		wsResumen["!cols"] = [{ wch: 32 }, { wch: 22 }, { wch: 22 }, { wch: 22 }];
		XLSX.utils.book_append_sheet(wb, wsResumen, "Resumen");

		// ── Hoja 2: Detalle Diario ────────────────────────────────────────────
		if (facturasPersonalizadasData.dias?.length > 0) {
			const diasHeaders = [
				"Fecha",
				"Total Facturas",
				"Personalizadas",
				"Genéricas",
				"% Personalizadas",
				"Subtotal Personalizadas",
				"Impuestos Personalizadas",
				"Total Personalizadas Valor",
				"Subtotal Genéricas",
				"Impuestos Genéricas",
				"Total Genéricas Valor",
			];
			const diasRows = facturasPersonalizadasData.dias.map((d) => [
				d.fecha,
				d.total_facturas,
				d.total_personalizadas,
				d.total_genericas,
				`${(d.porcentaje_personalizadas || 0).toFixed(2)}%`,
				d.subtotal_personalizadas,
				d.impuestos_personalizadas,
				d.total_personalizadas_valor,
				d.subtotal_genericas,
				d.impuestos_genericas,
				d.total_genericas_valor,
			]);
			// Totales
			const totalesRow = [
				"TOTAL",
				facturasPersonalizadasData.total_facturas,
				facturasPersonalizadasData.total_personalizadas,
				facturasPersonalizadasData.total_genericas,
				`${(facturasPersonalizadasData.porcentaje_personalizadas || 0).toFixed(2)}%`,
				facturasPersonalizadasData.subtotal_personalizadas,
				facturasPersonalizadasData.impuestos_personalizadas,
				facturasPersonalizadasData.total_personalizadas_valor,
				facturasPersonalizadasData.subtotal_genericas,
				facturasPersonalizadasData.impuestos_genericas,
				facturasPersonalizadasData.total_genericas_valor,
			];

			const wsDias = XLSX.utils.aoa_to_sheet([diasHeaders, ...diasRows, [], totalesRow]);
			wsDias["!cols"] = diasHeaders.map((_, i) => ({ wch: i === 0 ? 14 : 26 }));
			XLSX.utils.book_append_sheet(wb, wsDias, "Detalle Diario");
		}

		const nombreArchivo = `Analisis_Facturadores_${facturasPersonalizadasData.nitemisor}_${facturasPersonalizadasData.fecha_inicio}_${facturasPersonalizadasData.fecha_fin}.xlsx`;
		XLSX.writeFile(wb, nombreArchivo);
	};

	// Effects para Dashboard Ejecutivo
	useEffect(() => {
		if (empresaSeleccionada && isFormValid() && consultaMode === "año") {
			// Solo ejecutar automáticamente cuando cambie la empresa y esté en modo año
			fetchDashboardData();
			fetchSucursalesData();
			fetchComparativoDiarioData();
			fetchDocumentosGeneradosData();
			fetchIndicadoresGeneralesData();
		}
	}, [empresaSeleccionada]);

	// Efecto separado para cuando cambie el año en modo automático
	useEffect(() => {
		if (empresaSeleccionada && consultaMode === "año" && selectedYear) {
			fetchDashboardData();
			fetchSucursalesData();
			fetchComparativoDiarioData();
			fetchDocumentosGeneradosData();
			fetchIndicadoresGeneralesData();
		}
	}, [selectedYear]);

	// Efecto para cuando cambie el mes seleccionado
	useEffect(() => {
		if (empresaSeleccionada && selectedYear && selectedMonth) {
			fetchDocumentosGeneradosData();
			fetchIndicadoresGeneralesData();
		}
	}, [selectedMonth]);

	// Funciones de gráficos para Dashboard Ejecutivo
	const getTrendChartData = () => {
		if (!dashboardData?.tendencias_mensuales?.length) {
			return null;
		}

		// Mapear nombres de meses
		const nombresMeses = [
			'Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio',
			'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre'
		];

		const meses = (dashboardData.tendencias_mensuales || []).map(
			(item) => nombresMeses[item.mes - 1] || `Mes ${item.mes}`
		);
		const facturacion = (dashboardData.tendencias_mensuales || []).map(
			(item) => item.total_ventas || 0
		);
		const documentos = (dashboardData.tendencias_mensuales || []).map(
			(item) => item.total_lineas || 0
		);

		const chartData = {
			labels: meses,
			datasets: [
				{
					label: "Facturación",
					data: facturacion,
					borderColor: "rgb(75, 192, 192)",
					backgroundColor: "rgba(75, 192, 192, 0.2)",
					yAxisID: "y",
				},
				{
					label: "Líneas de Productos",
					data: documentos,
					borderColor: "rgb(255, 99, 132)",
					backgroundColor: "rgba(255, 99, 132, 0.2)",
					yAxisID: "y1",
				},
			],
		};

		return chartData;
	};

	const getTopSucursalesChartData = () => {
		if (
			!dashboardData?.top_sucursales ||
			!Array.isArray(dashboardData.top_sucursales)
		)
			return null;

		const sucursales = (dashboardData.top_sucursales || [])
			.slice(0, 5)
			.map((item) => item.sucursal);
		const facturacion = (dashboardData.top_sucursales || [])
			.slice(0, 5)
			.map((item) => item.facturacion);

		return {
			labels: sucursales,
			datasets: [
				{
					data: facturacion,
					backgroundColor: [
						"#FF6384",
						"#36A2EB",
						"#FFCE56",
						"#4BC0C0",
						"#9966FF",
					],
				},
			],
		};
	};

	const getComparativoDiarioChartData = () => {
		if (!comparativoDiarioData?.data?.length) return null;

		return {
			labels: (comparativoDiarioData.data || []).map((d) => `Día ${d.dia}`),
			datasets: [
				{
					label: `${comparativoDiarioData.mes_actual} ${comparativoDiarioData.year_actual}`,
					data: (comparativoDiarioData.data || []).map(
						(d) => d.facturacion_actual
					),
					borderColor: "rgb(54, 162, 235)",
					backgroundColor: "rgba(54, 162, 235, 0.1)",
					tension: 0.1,
				},
				{
					label: `${comparativoDiarioData.mes_anterior} ${comparativoDiarioData.year_anterior}`,
					data: (comparativoDiarioData.data || []).map(
						(d) => d.facturacion_anterior
					),
					borderColor: "rgb(255, 99, 132)",
					backgroundColor: "rgba(255, 99, 132, 0.1)",
					tension: 0.1,
				},
			],
		};
	};

	// Funciones de descarga Excel
	const downloadTipoDocumentoExcel = () => {
		if (!dashboardData?.resumen_ejecutivo?.por_tipo_documento?.length) return;

		const data = [
			// Encabezados
			["Tipo", "Descripción", "Documentos", "Facturación", "Subtotal", "Impuestos", "Participación", "Ticket Promedio", "Efecto"],
			// Datos
			...dashboardData.resumen_ejecutivo.por_tipo_documento.map(tipo => [
				tipo.tipo_comprobante,
				tipo.descripcion_tipo,
				tipo.total_documentos,
				tipo.facturacion_total,
				tipo.subtotal_total,
				tipo.impuesto_total,
				tipo.participacion,
				tipo.ticket_promedio,
				tipo.efecto_contable
			])
		];

		const wb = XLSX.utils.book_new();
		const ws = XLSX.utils.aoa_to_sheet(data);

		// Ajustar ancho de columnas
		ws['!cols'] = [
			{ width: 8 },  // Tipo
			{ width: 25 }, // Descripción
			{ width: 12 }, // Documentos
			{ width: 15 }, // Facturación
			{ width: 15 }, // Subtotal
			{ width: 15 }, // Impuestos
			{ width: 12 }, // Participación
			{ width: 15 }, // Ticket Promedio
			{ width: 10 }  // Efecto
		];

		XLSX.utils.book_append_sheet(wb, ws, "Análisis por Tipo de Documento");

		const fileName = `Analisis_Tipo_Documento_${dashboardData?.nitemisor || 'Dashboard'}_${toLocalDateString(new Date())}.xlsx`;
		XLSX.writeFile(wb, fileName);
	};

	const downloadTopSucursalesExcel = () => {
		if (!dashboardData?.top_sucursales?.length) return;

		const data = [
			// Encabezados
			["Posición", "Sucursal", "Store ID", "Facturación", "Documentos", "Participación", "Ticket Promedio"],
			// Datos
			...dashboardData.top_sucursales.map((sucursal, index) => [
				index + 1,
				sucursal.sucursal,
				sucursal.storeid,
				sucursal.facturacion,
				sucursal.documentos,
				sucursal.participacion,
				sucursal.ticket_promedio
			])
		];

		const wb = XLSX.utils.book_new();
		const ws = XLSX.utils.aoa_to_sheet(data);

		// Ajustar ancho de columnas
		ws['!cols'] = [
			{ width: 10 }, // Posición
			{ width: 25 }, // Sucursal
			{ width: 10 }, // Store ID
			{ width: 15 }, // Facturación
			{ width: 12 }, // Documentos
			{ width: 12 }, // Participación
			{ width: 15 }  // Ticket Promedio
		];

		XLSX.utils.book_append_sheet(wb, ws, "Top Sucursales");

		const fileName = `Top_Sucursales_${dashboardData?.nitemisor || 'Dashboard'}_${toLocalDateString(new Date())}.xlsx`;
		XLSX.writeFile(wb, fileName);
	};

	const downloadTopClientesExcel = () => {
		if (!dashboardData?.top_clientes?.length) return;

		const data = [
			// Encabezados
			["Posición", "Cliente", "Facturación", "Documentos", "Participación", "Ticket Promedio"],
			// Datos
			...dashboardData.top_clientes.map((cliente, index) => [
				index + 1,
				cliente.cliente,
				cliente.facturacion,
				cliente.documentos,
				cliente.participacion,
				cliente.ticket_promedio
			])
		];

		const wb = XLSX.utils.book_new();
		const ws = XLSX.utils.aoa_to_sheet(data);

		// Ajustar ancho de columnas
		ws['!cols'] = [
			{ width: 10 }, // Posición
			{ width: 35 }, // Cliente
			{ width: 15 }, // Facturación
			{ width: 12 }, // Documentos
			{ width: 12 }, // Participación
			{ width: 15 }  // Ticket Promedio
		];

		XLSX.utils.book_append_sheet(wb, ws, "Top Clientes");

		const fileName = `Top_Clientes_${dashboardData?.nitemisor || 'Dashboard'}_${toLocalDateString(new Date())}.xlsx`;
		XLSX.writeFile(wb, fileName);
	};

	const downloadSucursalesAnalisisExcel = () => {
		if (!sucursalesData?.data?.length) return;

		const data = [
			// Encabezados
			["Posición", "Sucursal", "Store ID", "Importe", "Documentos", "Ticket Promedio", "Participación", "Docs/Día"],
			// Datos
			...sucursalesData.data.map((sucursal, index) => [
				index + 1,
				sucursal.sucursal,
				sucursal.storeid,
				sucursal.importe,
				sucursal.documentos,
				sucursal.ticket_promedio,
				((sucursal.importe / sucursalesData.total_importe) * 100).toFixed(1) + '%',
				Math.round(sucursal.documentos / 30) // Asumiendo 30 días promedio
			])
		];

		const wb = XLSX.utils.book_new();
		const ws = XLSX.utils.aoa_to_sheet(data);

		// Ajustar ancho de columnas
		ws['!cols'] = [
			{ width: 10 }, // Posición
			{ width: 25 }, // Sucursal
			{ width: 10 }, // Store ID
			{ width: 15 }, // Importe
			{ width: 12 }, // Documentos
			{ width: 15 }, // Ticket Promedio
			{ width: 12 }, // Participación
			{ width: 10 }  // Docs/Día
		];

		XLSX.utils.book_append_sheet(wb, ws, "Análisis de Sucursales");

		const fileName = `Analisis_Sucursales_${sucursalesData?.nitemisor || 'Dashboard'}_${toLocalDateString(new Date())}.xlsx`;
		XLSX.writeFile(wb, fileName);
	};

	const downloadComparativoDiarioExcel = () => {
		if (!comparativoDiarioData?.data?.length) return;

		const data = [
			// Encabezados
			["Día", `${comparativoDiarioData.mes_actual} ${comparativoDiarioData.year_actual}`, `${comparativoDiarioData.mes_anterior} ${comparativoDiarioData.year_anterior}`, "Variación"],
			// Datos
			...comparativoDiarioData.data.map(dia => [
				dia.dia,
				dia.facturacion_actual,
				dia.facturacion_anterior,
				variacionToText(dia.variacion),
			])
		];

		const wb = XLSX.utils.book_new();
		const ws = XLSX.utils.aoa_to_sheet(data);

		// Ajustar ancho de columnas
		ws['!cols'] = [
			{ width: 8 },  // Día
			{ width: 18 }, // Mes actual
			{ width: 18 }, // Mes anterior
			{ width: 12 }  // Variación
		];

		XLSX.utils.book_append_sheet(wb, ws, "Comparativo Diario");

		const fileName = `Comparativo_Diario_${comparativoDiarioData?.nitemisor || 'Dashboard'}_${toLocalDateString(new Date())}.xlsx`;
		XLSX.writeFile(wb, fileName);
	};

	const downloadDocumentosGeneradosExcel = () => {
		if (!documentosGeneradosData?.tipos_documento?.length) return;

		const mesNombre = documentosGeneradosData.periodo_mes || '';
		const data = [
			// Encabezados
			["DOCUMENTOS GENERADOS"],
			[`${documentosGeneradosData.nombre_emisor || ''} (${documentosGeneradosData.nitemisor || ''})`],
			[],
			["TIPO DE DOCUMENTO", `${mesNombre.toUpperCase()} ${selectedYear}`, "", "ACUMULADO", ""],
			["", "CANT.", "VALOR", "CANT.", "VALOR"],
			// Datos
			...documentosGeneradosData.tipos_documento.map(doc => [
				doc.tipo_documento,
				doc.periodo?.cantidad || 0,
				doc.periodo?.valor || 0,
				doc.acumulado?.cantidad || 0,
				doc.acumulado?.valor || 0
			]),
			// Totales
			["TOTALES",
				documentosGeneradosData.totales?.periodo?.cantidad || 0,
				documentosGeneradosData.totales?.periodo?.valor || 0,
				documentosGeneradosData.totales?.acumulado?.cantidad || 0,
				documentosGeneradosData.totales?.acumulado?.valor || 0
			]
		];

		const wb = XLSX.utils.book_new();
		const ws = XLSX.utils.aoa_to_sheet(data);

		// Ajustar ancho de columnas
		ws['!cols'] = [
			{ width: 35 }, // Tipo documento
			{ width: 12 }, // Cant mes
			{ width: 18 }, // Valor mes
			{ width: 12 }, // Cant acumulado
			{ width: 18 }  // Valor acumulado
		];

		// Combinar celdas para encabezados
		ws['!merges'] = [
			{ s: { r: 0, c: 0 }, e: { r: 0, c: 4 } }, // Título
			{ s: { r: 1, c: 0 }, e: { r: 1, c: 4 } }, // Empresa
			{ s: { r: 3, c: 1 }, e: { r: 3, c: 2 } }, // Mes actual
			{ s: { r: 3, c: 3 }, e: { r: 3, c: 4 } }  // Acumulado
		];

		XLSX.utils.book_append_sheet(wb, ws, "Documentos Generados");

		const fileName = `Documentos_Generados_${documentosGeneradosData.nitemisor || 'Dashboard'}_${mesNombre}_${selectedYear}.xlsx`;
		XLSX.writeFile(wb, fileName);
	};

	// Función para descargar Excel de Indicadores Generales
	const downloadIndicadoresGeneralesExcel = () => {
		if (!indicadoresGeneralesData?.indicadores?.length) return;

		const data = [
			// Encabezados principales
			["INDICADORES GENERALES - DOCUMENTOS GENERADOS"],
			[`${indicadoresGeneralesData.nombre_emisor || ''} (${indicadoresGeneralesData.nitemisor || ''})`],
			[`AÑO ${selectedYear}`],
			[],
			// Encabezados de columnas
			["INDICADORES GENERALES", ...indicadoresGeneralesData.indicadores.map(m => m.nombre_mes?.toUpperCase() || '')],
			[],
			// Fila 1: Cantidad total
			["Cantidad total de documentos generados (1)", ...indicadoresGeneralesData.indicadores.map(m => m.cantidad_total_documentos || 0)],
			[],
			// Fila 2: Promedio diario cantidad
			["Número promedio diario de documentos (2)", ...indicadoresGeneralesData.indicadores.map(m => m.numero_promedio_diario || 0)],
			[],
			// Fila 3: Valor total
			["Valor total de las operaciones realizadas (3)", ...indicadoresGeneralesData.indicadores.map(m => m.valor_total_operaciones || 0)],
			[],
			// Fila 4: Promedio diario valor
			["Valor promedio diario de las operaciones (2)(3)", ...indicadoresGeneralesData.indicadores.map(m => m.valor_promedio_diario_ops || 0)],
			[],
			[],
			["NOTAS:"],
			["(1) Incluye todos los documentos generados en el mes"],
			["(2) Calculado basado en días reales del mes"],
			["(3) Las notas de crédito se restan en el cálculo de valores"]
		];

		const wb = XLSX.utils.book_new();
		const ws = XLSX.utils.aoa_to_sheet(data);

		// Ajustar ancho de columnas
		const colWidths = [{ width: 50 }]; // Primera columna (indicadores)
		indicadoresGeneralesData.indicadores.forEach(() => {
			colWidths.push({ width: 18 }); // Columnas de meses
		});
		ws['!cols'] = colWidths;

		// Combinar celdas para encabezados
		const numMeses = indicadoresGeneralesData.indicadores.length;
		ws['!merges'] = [
			{ s: { r: 0, c: 0 }, e: { r: 0, c: numMeses } }, // Título
			{ s: { r: 1, c: 0 }, e: { r: 1, c: numMeses } }, // Empresa
			{ s: { r: 2, c: 0 }, e: { r: 2, c: numMeses } }  // Año
		];

		XLSX.utils.book_append_sheet(wb, ws, "Indicadores Generales");

		const fileName = `Indicadores_Generales_${indicadoresGeneralesData.nitemisor || 'Dashboard'}_${selectedYear}.xlsx`;
		XLSX.writeFile(wb, fileName);
	};

	return (
		<React.Fragment>
			<div className="erp-dashboard erp-fade-in">
			{/* ═══ Enterprise Context Header ═══ */}
			<div className="erp-context-header">
				<div className="d-flex justify-content-between align-items-start flex-wrap gap-3" style={{ position: 'relative', zIndex: 1 }}>
					<div>
						<h1 className="erp-header-title">
							<i className="mdi mdi-view-dashboard-outline me-2" style={{ opacity: 0.7 }}></i>
							Dashboard Ejecutivo
						</h1>
						<div className="erp-header-subtitle">
							{dashboardData?.nombreemisor || empresaSeleccionada?.nombre || 'Seleccione una empresa'}
						</div>
						<div className="erp-header-period mt-2">
							<i className="mdi mdi-calendar-range"></i>
							{consultaMode === "año"
								? (
									typeof dashboardData?.periodo === "string"
										? dashboardData.periodo
										: (dashboardData?.periodo?.label
											|| dashboardData?.periodo?.nombre
											|| selectedYear)
								)
								: consultaMode === "rango" && fechaInicio && fechaFin
									? `${fechaInicio} — ${fechaFin}`
									: "Seleccione período"
							}
						</div>
					</div>
					<div className="erp-filter-bar">
						{/* Mode toggle */}
						<div className="erp-filter-group">
							<input type="radio" id="erp-mode-year" name="erpConsultaMode" value="año" checked={consultaMode === "año"} onChange={(e) => handleConsultaModeChange(e.target.value)} className="erp-filter-radio" />
							<label htmlFor="erp-mode-year" className="erp-filter-label">Por Año</label>
							<input type="radio" id="erp-mode-range" name="erpConsultaMode" value="rango" checked={consultaMode === "rango"} onChange={(e) => handleConsultaModeChange(e.target.value)} className="erp-filter-radio" />
							<label htmlFor="erp-mode-range" className="erp-filter-label">Por Rango</label>
						</div>

						{consultaMode === "año" ? (
							<>
								<select className="erp-filter-select" value={selectedYear} onChange={(e) => setSelectedYear(parseInt(e.target.value))}>
									<option value={2026}>2026</option>
									<option value={2025}>2025</option>
									<option value={2024}>2024</option>
									<option value={2023}>2023</option>
								</select>
								{activeTab === "4" && (
									<select className="erp-filter-select" value={selectedMonth} onChange={(e) => setSelectedMonth(parseInt(e.target.value))}>
										<option value={1}>Enero</option><option value={2}>Febrero</option><option value={3}>Marzo</option>
										<option value={4}>Abril</option><option value={5}>Mayo</option><option value={6}>Junio</option>
										<option value={7}>Julio</option><option value={8}>Agosto</option><option value={9}>Septiembre</option>
										<option value={10}>Octubre</option><option value={11}>Noviembre</option><option value={12}>Diciembre</option>
									</select>
								)}
							</>
						) : (
							<>
								<input type="date" className="erp-filter-input" value={fechaInicio} onChange={handleFechaInicioChange} />
								<input type="date" className="erp-filter-input" value={fechaFin} onChange={handleFechaFinChange} min={fechaInicio} />
							</>
						)}

						<button className="erp-btn-query" id="btn-consultar-dashboard" disabled={!isFormValid() || isLoadingDashboard || isLoadingSucursales || isLoadingComparativo} onClick={() => { if (isFormValid()) { fetchDashboardData(); fetchSucursalesData(); fetchComparativoDiarioData(); } }}>
							{(isLoadingDashboard || isLoadingSucursales || isLoadingComparativo) ? (<><Spinner size="sm" className="me-1" />Procesando...</>) : (<><i className="mdi mdi-magnify"></i>Consultar</>)}
						</button>
						{!isFormValid() && (<UncontrolledTooltip target="btn-consultar-dashboard" placement="bottom">{!empresaSeleccionada ? "Seleccione una empresa" : consultaMode === "año" && !selectedYear ? "Seleccione un año" : consultaMode === "rango" && (!fechaInicio || !fechaFin) ? "Complete las fechas" : consultaMode === "rango" && fechaInicio > fechaFin ? "Fecha inicio debe ser menor" : "Complete los campos"}</UncontrolledTooltip>)}
						<button className="erp-btn-reset" onClick={() => { setConsultaMode("año"); setSelectedYear(new Date().getFullYear()); setFechaInicio(""); setFechaFin(""); setDashboardData(null); setSucursalesData(null); setComparativoDiarioData(null); }}>
							<i className="mdi mdi-refresh"></i>Limpiar
						</button>
					</div>
				</div>
			</div>

			{/* ═══ Tab Navigation ═══ */}
			<div className="erp-tabs-wrapper">
				<div className="erp-tabs-nav">
								<button className={`erp-tab-item ${activeTab === "1" ? "active" : ""}`} onClick={() => setActiveTab("1")}><i className="fas fa-chart-line"></i>Dashboard Ejecutivo</button>
								<button className={`erp-tab-item ${activeTab === "2" ? "active" : ""}`} onClick={() => setActiveTab("2")}><i className="fas fa-store"></i>Sucursales</button>
								<button className={`erp-tab-item ${activeTab === "3" ? "active" : ""}`} onClick={() => setActiveTab("3")}><i className="fas fa-exchange-alt"></i>Comparativo Diario</button>
								<button className={`erp-tab-item ${activeTab === "4" ? "active" : ""}`} onClick={() => setActiveTab("4")}><i className="fas fa-file-invoice"></i>Documentos Generados</button>
								<button className={`erp-tab-item ${activeTab === "5" ? "active" : ""}`} onClick={() => setActiveTab("5")}><i className="fas fa-users"></i>Análisis Facturadores</button>
							</div>

							<TabContent activeTab={activeTab}>
								{/* Tab 1: Dashboard Ejecutivo */}
								<TabPane tabId="1" className="erp-tab-content">
									{isLoadingDashboard ? (
										<div className="erp-skeleton">
											<div className="erp-skeleton-spinner"></div>
											<div className="erp-skeleton-text">Cargando Dashboard Ejecutivo</div>
											<div className="erp-skeleton-subtext">Procesando datos analíticos...</div>
										</div>
									) : (
										<>
											{/* Resumen Ejecutivo Principal */}
											<Row className="mb-4">
												<Col xl="12">
													<Card className="shadow-sm">
														<CardHeader className="bg-light border-0">
															<h6 className="mb-0 text-dark">
																<i className="fas fa-chart-bar me-2"></i>
																Resumen Ejecutivo -{" "}
																{typeof dashboardData?.periodo === "string"
																	? dashboardData.periodo
																	: (dashboardData?.periodo?.label
																		|| dashboardData?.periodo?.nombre
																		|| selectedYear)}
															</h6>
															<small className="text-muted">
																{dashboardData?.fecha_inicio &&
																	dashboardData?.fecha_fin &&
																	`${dashboardData.fecha_inicio} - ${dashboardData.fecha_fin}`}
															</small>
														</CardHeader>
														<CardBody className="p-3">
															{/* Métricas Principales */}
															<Row className="mb-4">
																<Col lg="2" md="4" sm="6" className="mb-3">
																	<div className="border rounded p-3 text-center bg-light">
																		<div
																			className="text-muted text-uppercase mb-1"
																			style={{ fontSize: "0.7rem" }}>
																			<strong>Facturación Total</strong>
																		</div>
																		<div className="h4 mb-0 font-weight-bold text-primary">
																			{formatoCOP.format(
																				dashboardData?.resumen_ejecutivo
																					?.facturacion_total || 0
																			)}
																		</div>
																		<small className="text-muted">total </small>
																	</div>
																</Col>
																<Col lg="2" md="4" sm="6" className="mb-3">
																	<div className="border rounded p-3 text-center bg-light">
																		<div
																			className="text-muted text-uppercase mb-1"
																			style={{ fontSize: "0.7rem" }}>
																			<strong>Crecimiento vs Año Anterior</strong>
																		</div>
																		<div className="h4 mb-0 font-weight-bold text-success">
																			{dashboardData?.resumen_ejecutivo
																				?.crecimiento_vs_anterior || "N/A"}
																		</div>
																		<small className="text-muted">
																			vs año anterior
																		</small>
																	</div>
																</Col>
																<Col lg="2" md="4" sm="6" className="mb-3">
																	<div className="border rounded p-3 text-center bg-light">
																		<div
																			className="text-muted text-uppercase mb-1"
																			style={{ fontSize: "0.7rem" }}>
																			<strong>Total Documentos</strong>
																		</div>
																		<div className="h4 mb-0 font-weight-bold text-info">
																			{formatoNumero.format(
																				dashboardData?.resumen_ejecutivo
																					?.total_documentos || 0
																			)}
																		</div>
																		<small className="text-muted">documentos</small>
																	</div>
																</Col>
																<Col lg="2" md="4" sm="6" className="mb-3">
																	<div className="border rounded p-3 text-center bg-light">
																		<div
																			className="text-muted text-uppercase mb-1"
																			style={{ fontSize: "0.7rem" }}>
																			<strong>Ticket Promedio</strong>
																		</div>
																		<div className="h4 mb-0 font-weight-bold text-warning">
																			{formatoCOP.format(
																				dashboardData?.resumen_ejecutivo
																					?.ticket_promedio || 0
																			)}
																		</div>
																		<small className="text-muted">
																			por documento
																		</small>
																	</div>
																</Col>
																<Col lg="2" md="4" sm="6" className="mb-3">
																	<div className="border rounded p-3 text-center bg-light">
																		<div
																			className="text-muted text-uppercase mb-1"
																			style={{ fontSize: "0.7rem" }}>
																			<strong>Subtotal Total</strong>
																		</div>
																		<div className="h4 mb-0 font-weight-bold text-secondary">
																			{formatoCOP.format(
																				dashboardData?.resumen_ejecutivo
																					?.subtotal_total || 0
																			)}
																		</div>
																		<small className="text-muted">
																			antes impuestos
																		</small>
																	</div>
																</Col>
																<Col lg="2" md="4" sm="6" className="mb-3">
																	<div className="border rounded p-3 text-center bg-light">
																		<div
																			className="text-muted text-uppercase mb-1"
																			style={{ fontSize: "0.7rem" }}>
																			<strong>Sucursales Activas</strong>
																		</div>
																		<div className="h4 mb-0 font-weight-bold text-dark">
																			{dashboardData?.resumen_ejecutivo
																				?.sucursales_activas || 0}
																		</div>
																		<small className="text-muted">sucursales</small>
																	</div>
																</Col>
															</Row>

															{/* Segunda fila de métricas */}
															<Row className="mb-4">
																<Col lg="2" md="4" sm="6" className="mb-3">
																	<div className="border rounded p-3 text-center bg-light">
																		<div
																			className="text-muted text-uppercase mb-1"
																			style={{ fontSize: "0.7rem" }}>
																			<strong>Impuestos Total</strong>
																		</div>
																		<div className="h4 mb-0 font-weight-bold text-primary">
																			{formatoCOP.format(
																				dashboardData?.resumen_ejecutivo
																					?.impuesto_total || 0
																			)}
																		</div>
																		<small className="text-muted">
																			IVA + otros
																		</small>
																	</div>
																</Col>
															</Row>

															{/* Análisis por Tipo de Documento */}
															{dashboardData?.resumen_ejecutivo
																?.por_tipo_documento &&
																Array.isArray(
																	dashboardData.resumen_ejecutivo.por_tipo_documento
																) &&
																dashboardData.resumen_ejecutivo.por_tipo_documento
																	.length > 0 && (
																	<Row className="mt-4">
																		<Col xl="12">
																			<div className="d-flex justify-content-between align-items-center mb-3">
																				<h6 className="text-muted mb-0">
																					<i className="fas fa-file-alt me-2"></i>
																					Análisis por Tipo de Documento
																				</h6>
																				<Button
																					color="success"
																					size="sm"
																					onClick={downloadTipoDocumentoExcel}
																					className="d-flex align-items-center">
																					<i className="fas fa-download me-1"></i>
																					Excel
																				</Button>
																			</div>
																			<div className="table-responsive">
																				<Table hover className="mb-0">
																					<thead className="table-light">
																						<tr>
																							<th style={{ width: "80px" }}>
																								Tipo
																							</th>
																							<th>Descripción</th>
																							<th className="text-end">
																								Documentos
																							</th>
																							<th className="text-end">
																								Facturación
																							</th>
																							<th className="text-end">Subtotal</th>
																							<th className="text-end">
																								Impuestos
																							</th>
																							<th className="text-center">
																								Participación
																							</th>
																							<th className="text-end">
																								Ticket Promedio
																							</th>
																						</tr>
																					</thead>
																					<tbody>
																						{(
																							dashboardData?.resumen_ejecutivo
																								?.por_tipo_documento || []
																						).map((tipo, index) => (
																							<tr key={index}>
																								<td>
																									<Badge
																										color={
																											tipo.efecto_contable ===
																												"SUMA"
																												? "success"
																												: "warning"
																										}
																										className="font-monospace">
																										{tipo.tipo_comprobante}
																									</Badge>
																								</td>
																								<td>
																									<strong>
																										{getDescripcionTipo(tipo)}
																									</strong>
																									<br />
																									<small className="text-muted">
																										{tipo.efecto_contable === "SUMA"
																											? "Suma"
																											: "Resta"}
																									</small>
																								</td>
																								<td className="text-end">
																									<strong>
																										{formatoNumero.format(
																											tipo.total_documentos
																										)}
																									</strong>
																								</td>
																								<td className="text-end">
																									<strong>
																										{formatoCOP.format(
																											tipo.facturacion_total
																										)}
																									</strong>
																								</td>
																								<td className="text-end">
																									<span className="text-muted">
																										{formatoCOP.format(
																											tipo.subtotal_total
																										)}
																									</span>
																								</td>
																								<td className="text-end">
																									<span className="text-muted">
																										{formatoCOP.format(
																											tipo.impuesto_total
																										)}
																									</span>
																								</td>
																								<td className="text-center">
																									<Badge
																										color={
																											parseFloat(
																												tipo.participacion
																											) > 90
																												? "success"
																												: parseFloat(
																													tipo.participacion
																												) > 5
																													? "info"
																													: "secondary"
																										}
																										pill>
																										{tipo.participacion}
																									</Badge>
																								</td>
																								<td className="text-end">
																									<strong>
																										{formatoCOP.format(
																											tipo.ticket_promedio
																										)}
																									</strong>
																								</td>
																							</tr>
																						))}
																					</tbody>
																				</Table>
																			</div>
																		</Col>
																	</Row>
																)}
														</CardBody>
													</Card>
												</Col>
											</Row>

											{/* Gráfico de Tendencias y Análisis Tributario */}
											<Row className="mb-4">
												<Col xl="8" lg="12">
													<Card className="shadow-sm">
														<CardHeader className="bg-light d-flex justify-content-between align-items-center border-0">
															<h6 className="mb-0 text-dark">
																<i className="fas fa-chart-line me-2"></i>
																Tendencias Mensuales {selectedYear}
															</h6>
														</CardHeader>
														<CardBody>
															{(() => {
																const chartData = getTrendChartData();

																if (chartData) {
																	return (
																		<div style={{ width: "100%", height: "350px" }}>
																			<Line
																				data={chartData}
																				options={{
																					responsive: true,
																					maintainAspectRatio: false,
																					plugins: {
																						legend: { position: "top" },
																						title: { display: false },
																					},
																					scales: {
																						x: {
																							title: { display: true, text: "Mes" },
																						},
																						y: {
																							type: "linear",
																							display: true,
																							position: "left",
																							title: {
																								display: true,
																								text: "Facturación",
																							},
																						},
																						y1: {
																							type: "linear",
																							display: true,
																							position: "right",
																							title: {
																								display: true,
																								text: "Líneas de Productos",
																							},
																							grid: { drawOnChartArea: false },
																						},
																					},
																				}}
																			/>
																		</div>
																	);
																} else {
																	return (
																		<div className="text-center py-5">
																			<i className="fas fa-chart-line fa-3x text-muted mb-3"></i>
																			<p className="text-muted">No hay datos disponibles para mostrar</p>
																		</div>
																	);
																}
															})()}
														</CardBody>
													</Card>
												</Col>

												{/* Panel Lateral - Análisis Tributario */}
												<Col xl="4" lg="12">
													<Card className="shadow-sm">
														<CardHeader className="bg-light border-0">
															<h6 className="mb-0 text-dark">
																<i className="fas fa-calculator me-2"></i>
																Análisis Tributario
															</h6>
														</CardHeader>
														<CardBody className="p-3">
															<div className="row g-3">
																<div className="col-12">
																	<div className="d-flex justify-content-between border-bottom pb-2">
																		<span className="text-muted">
																			Base Gravable
																		</span>
																		<span className="fw-bold">
																			{formatoCOP.format(
																				dashboardData?.analisis_tributario
																					?.base_gravable || 0
																			)}
																		</span>
																	</div>
																</div>
																<div className="col-12">
																	<div className="d-flex justify-content-between border-bottom pb-2">
																		<span className="text-muted">IVA 19%</span>
																		<span className="fw-bold text-success">
																			{formatoCOP.format(
																				dashboardData?.analisis_tributario
																					?.iva_19 || 0
																			)}
																		</span>
																	</div>
																</div>
																<div className="col-12">
																	<div className="d-flex justify-content-between border-bottom pb-2">
																		<span className="text-muted">IVA Total</span>
																		<span className="fw-bold text-info">
																			{formatoCOP.format(
																				dashboardData?.analisis_tributario
																					?.iva_total || 0
																			)}
																		</span>
																	</div>
																</div>
																<div className="col-12">
																	<div className="d-flex justify-content-between border-bottom pb-2">
																		<span className="text-muted">Retenciones</span>
																		<span className="fw-bold text-warning">
																			{formatoCOP.format(
																				dashboardData?.analisis_tributario
																					?.retenciones || 0
																			)}
																		</span>
																	</div>
																</div>
																<div className="col-12">
																	<div className="d-flex justify-content-between bg-light p-2 rounded">
																		<span className="fw-bold">Total Impuestos</span>
																		<span className="fw-bold text-primary">
																			{formatoCOP.format(
																				(dashboardData?.analisis_tributario
																					?.iva_total || 0) +
																				(dashboardData?.analisis_tributario
																					?.retenciones || 0) +
																				(dashboardData?.analisis_tributario
																					?.otros_impuestos || 0)
																			)}
																		</span>
																	</div>
																</div>
															</div>

															{/* Top 5 Sucursales Chart */}
															{getTopSucursalesChartData() && (
																<div className="mt-4">
																	<h6 className="text-muted mb-3">
																		<i className="fas fa-store me-2"></i>
																		Top 5 Sucursales
																	</h6>
																	<div style={{ width: "100%", height: "200px" }}>
																		<Doughnut
																			data={getTopSucursalesChartData()}
																			options={{
																				responsive: true,
																				maintainAspectRatio: false,
																				plugins: {
																					legend: {
																						position: "bottom",
																						labels: {
																							boxWidth: 12,
																							font: { size: 10 },
																						},
																					},
																					title: { display: false },
																				},
																			}}
																		/>
																	</div>
																</div>
															)}
														</CardBody>
													</Card>
												</Col>
											</Row>

											{/* Top Sucursales y Top Clientes */}
											<Row className="mb-4">
												<Col lg="6" md="12">
													<Card className="shadow-sm">
														<CardHeader className="bg-light d-flex justify-content-between align-items-center border-0">
															<h6 className="mb-0 text-dark">
																<i className="fas fa-store me-2"></i>
																Top 10 Sucursales
															</h6>
															<Button
																color="success"
																size="sm"
																onClick={downloadTopSucursalesExcel}
																className="d-flex align-items-center">
																<i className="fas fa-download me-1"></i>
																Excel
															</Button>
														</CardHeader>
														<CardBody className="p-0">
															<div className="table-responsive">
																<Table hover className="mb-0">
																	<thead className="table-light">
																		<tr>
																			<th style={{ width: "40px" }}>#</th>
																			<th>Sucursal</th>
																			<th style={{ width: "60px" }}>ID</th>
																			<th className="text-end">Facturación</th>
																			<th className="text-end">Docs</th>
																			<th className="text-center">Part.</th>
																			<th className="text-end">Ticket</th>
																		</tr>
																	</thead>
																	<tbody>
																		{dashboardData?.top_sucursales &&
																			dashboardData.top_sucursales.length > 0 ? (
																			dashboardData.top_sucursales
																				.slice(0, 10)
																				.map((sucursal, index) => (
																					<tr key={index}>
																						<td>
																							<Badge
																								color={
																									index < 3
																										? "success"
																										: "secondary"
																								}
																								pill>
																								{index + 1}
																							</Badge>
																						</td>
																						<td>
																							<strong>{sucursal.sucursal}</strong>
																						</td>
																						<td>
																							<Badge
																								color="light"
																								className="font-monospace">
																								{sucursal.storeid}
																							</Badge>
																						</td>
																						<td className="text-end">
																							<strong>
																								{formatoCOP.format(
																									sucursal.facturacion
																								)}
																							</strong>
																						</td>
																						<td className="text-end">
																							<span className="text-muted">
																								{formatoNumero.format(
																									sucursal.documentos
																								)}
																							</span>
																						</td>
																						<td className="text-center">
																							<Badge
																								color={
																									parseFloat(
																										sucursal.participacion
																									) > 3
																										? "success"
																										: parseFloat(
																											sucursal.participacion
																										) > 2
																											? "info"
																											: "secondary"
																								}
																								pill>
																								{sucursal.participacion}
																							</Badge>
																						</td>
																						<td className="text-end">
																							<small className="text-muted">
																								{formatoCOP.format(
																									sucursal.ticket_promedio
																								)}
																							</small>
																						</td>
																					</tr>
																				))
																		) : (
																			<tr>
																				<td
																					colSpan="7"
																					className="text-center text-muted py-4">
																					No hay datos disponibles
																				</td>
																			</tr>
																		)}
																	</tbody>
																</Table>
															</div>
														</CardBody>
													</Card>
												</Col>
												<Col lg="6" md="12">
													<Card className="shadow-sm">
														<CardHeader className="bg-light d-flex justify-content-between align-items-center border-0">
															<h6 className="mb-0 text-dark">
																<i className="fas fa-users me-2"></i>
																Top 10 Clientes
															</h6>
															<Button
																color="success"
																size="sm"
																onClick={downloadTopClientesExcel}
																className="d-flex align-items-center">
																<i className="fas fa-download me-1"></i>
																Excel
															</Button>
														</CardHeader>
														<CardBody className="p-0">
															<div className="table-responsive">
																<Table hover className="mb-0">
																	<thead className="table-light">
																		<tr>
																			<th style={{ width: "40px" }}>#</th>
																			<th>Cliente</th>
																			<th className="text-end">Facturación</th>
																			<th className="text-end">Docs</th>
																			<th className="text-center">Part.</th>
																			<th className="text-end">Ticket</th>
																		</tr>
																	</thead>
																	<tbody>
																		{dashboardData?.top_clientes &&
																			dashboardData.top_clientes.length > 0 ? (
																			dashboardData.top_clientes
																				.slice(0, 10)
																				.map((cliente, index) => (
																					<tr key={index}>
																						<td>
																							<Badge
																								color={
																									index < 3
																										? "success"
																										: "secondary"
																								}
																								pill>
																								{index + 1}
																							</Badge>
																						</td>
																						<td>
																							<div>
																								<strong
																									className="text-truncate d-block"
																									style={{ maxWidth: "200px" }}>
																									{cliente.cliente}
																								</strong>
																								{index < 3 && (
																									<small className="text-muted">
																										Cliente principal
																									</small>
																								)}
																							</div>
																						</td>
																						<td className="text-end">
																							<strong>
																								{formatoCOP.format(
																									cliente.facturacion
																								)}
																							</strong>
																						</td>
																						<td className="text-end">
																							<span className="text-muted">
																								{formatoNumero.format(
																									cliente.documentos
																								)}
																							</span>
																						</td>
																						<td className="text-center">
																							<Badge
																								color={
																									parseFloat(
																										cliente.participacion
																									) > 20
																										? "success"
																										: parseFloat(
																											cliente.participacion
																										) > 5
																											? "info"
																											: "secondary"
																								}
																								pill>
																								{cliente.participacion}
																							</Badge>
																						</td>
																						<td className="text-end">
																							<small className="text-muted">
																								{formatoCOP.format(
																									cliente.ticket_promedio
																								)}
																							</small>
																						</td>
																					</tr>
																				))
																		) : (
																			<tr>
																				<td
																					colSpan="6"
																					className="text-center text-muted py-4">
																					No hay datos disponibles
																				</td>
																			</tr>
																		)}
																	</tbody>
																</Table>
															</div>
														</CardBody>
													</Card>
												</Col>
											</Row>

											{/* Métricas Comparativas y Alertas */}
										</>
									)}
								</TabPane>

								{/* Tab 2: Sucursales */}
								<TabPane tabId="2" className="p-4">
									{isLoadingSucursales ? (
										<div className="text-center py-5">
											<Spinner color="success" style={{ width: '3rem', height: '3rem' }} />
											<div className="mt-3">
												<h5 className="text-muted">Cargando Datos de Sucursales...</h5>
												<p className="text-muted mb-0">Procesando información por puntos de venta</p>
											</div>
										</div>
									) : (
										<>
											{sucursalesData && (
												<>
													{/* Resumen Ejecutivo de Sucursales */}
													<Row className="mb-4">
														<Col xl="12">
															<Card className="shadow-sm">
																<CardHeader className="bg-light border-0">
																	<h6 className="mb-0 text-dark">
																		<i className="fas fa-store me-2"></i>
																		Análisis de Sucursales - {selectedYear}
																	</h6>
																	<small className="text-muted">
																		{sucursalesData?.fecha_inicio || 'N/A'} -{" "}
																		{sucursalesData?.fecha_fin || 'N/A'}
																	</small>
																</CardHeader>
																<CardBody className="p-3">
																	{/* Tarjetas de Resumen */}
																	<Row className="mb-4">
																		<Col lg="3" md="6" className="mb-3">
																			<div className="border rounded p-3 text-center bg-light">
																				<div
																					className="text-muted text-uppercase mb-1"
																					style={{ fontSize: "0.7rem" }}>
																					<strong>Total Sucursales</strong>
																				</div>
																				<div className="h4 mb-0 font-weight-bold text-primary">
																					{sucursalesData?.total_sucursales || 0}
																				</div>
																				<small className="text-muted">
																					sucursales activas
																				</small>
																			</div>
																		</Col>
																		<Col lg="3" md="6" className="mb-3">
																			<div className="border rounded p-3 text-center bg-light">
																				<div
																					className="text-muted text-uppercase mb-1"
																					style={{ fontSize: "0.7rem" }}>
																					<strong>Importe Total</strong>
																				</div>
																				<div className="h4 mb-0 font-weight-bold text-success">
																					{formatoCOP.format(
																						sucursalesData?.total_importe || 0
																					)}
																				</div>
																				<small className="text-muted">
																					facturación total
																				</small>
																			</div>
																		</Col>
																		<Col lg="3" md="6" className="mb-3">
																			<div className="border rounded p-3 text-center bg-light">
																				<div
																					className="text-muted text-uppercase mb-1"
																					style={{ fontSize: "0.7rem" }}>
																					<strong>Total Documentos</strong>
																				</div>
																				<div className="h4 mb-0 font-weight-bold text-info">
																					{formatoNumero.format(
																						sucursalesData?.total_documentos || 0
																					)}
																				</div>
																				<small className="text-muted">
																					documentos emitidos
																				</small>
																			</div>
																		</Col>
																		<Col lg="3" md="6" className="mb-3">
																			<div className="border rounded p-3 text-center bg-light">
																				<div
																					className="text-muted text-uppercase mb-1"
																					style={{ fontSize: "0.7rem" }}>
																					<strong>Ticket Promedio</strong>
																				</div>
																				<div className="h4 mb-0 font-weight-bold text-warning">
																					{formatoCOP.format(
																						sucursalesData?.total_importe && sucursalesData?.total_documentos
																							? sucursalesData.total_importe / sucursalesData.total_documentos
																							: 0
																					)}
																				</div>
																				<small className="text-muted">
																					por documento
																				</small>
																			</div>
																		</Col>
																	</Row>

																	{/* Tabla Detallada de Sucursales */}
																	<Row className="mb-4">
																		<Col xl="12">
																			<Card className="shadow-sm">
																				<CardHeader className="bg-light d-flex justify-content-between align-items-center border-0">
																					<h6 className="mb-0 text-dark">
																						<i className="fas fa-table me-2"></i>
																						Ranking Detallado de Sucursales
																					</h6>
																					<Button
																						color="success"
																						size="sm"
																						onClick={downloadSucursalesAnalisisExcel}
																						className="d-flex align-items-center">
																						<i className="fas fa-download me-1"></i>
																						Excel
																					</Button>
																				</CardHeader>
																				<CardBody className="p-0">
																					<div className="table-responsive">
																						<Table hover className="mb-0">
																							<thead className="table-light">
																								<tr>
																									<th style={{ width: "40px" }}>
																										Pos
																									</th>
																									<th>Sucursal</th>
																									<th style={{ width: "80px" }}>
																										Store ID
																									</th>
																									<th className="text-end">
																										Importe
																									</th>
																									<th className="text-end">
																										Documentos
																									</th>
																									<th className="text-end">
																										Ticket Promedio
																									</th>
																									<th className="text-center">
																										Participación
																									</th>
																									<th className="text-end">
																										Docs/Día
																									</th>
																								</tr>
																							</thead>
																							<tbody>
																								{(sucursalesData?.data || []).map(
																									(sucursal, index) => {
																										const participacion = (
																											(sucursal.importe /
																												sucursalesData.total_importe) *
																											100
																										).toFixed(2);
																										const docsPorDia = Math.round(
																											sucursal.documentos / 365
																										);
																										const ticketPromedio =
																											sucursal.importe /
																											sucursal.documentos;

																										return (
																											<tr key={index}>
																												<td>
																													<Badge
																														color={
																															index < 3
																																? "success"
																																: index < 10
																																	? "info"
																																	: "secondary"
																														}
																														pill>
																														{index + 1}
																													</Badge>
																												</td>
																												<td>
																													<strong>
																														{sucursal.sucursal}
																													</strong>
																													{index < 5 && (
																														<>
																															<br />
																															<small className="text-success">
																																Top performer
																															</small>
																														</>
																													)}
																												</td>
																												<td>
																													<Badge
																														color="light"
																														className="font-monospace">
																														{sucursal.storeid}
																													</Badge>
																												</td>
																												<td className="text-end">
																													<strong>
																														{formatoCOP.format(
																															sucursal.importe
																														)}
																													</strong>
																												</td>
																												<td className="text-end">
																													<span className="text-muted">
																														{formatoNumero.format(
																															sucursal.documentos
																														)}
																													</span>
																												</td>
																												<td className="text-end">
																													<strong>
																														{formatoCOP.format(
																															ticketPromedio
																														)}
																													</strong>
																												</td>
																												<td className="text-center">
																													<Badge
																														color={
																															parseFloat(
																																participacion
																															) > 3
																																? "success"
																																: parseFloat(
																																	participacion
																																) > 2
																																	? "info"
																																	: parseFloat(
																																		participacion
																																	) > 1
																																		? "warning"
																																		: "secondary"
																														}
																														pill>
																														{participacion}%
																													</Badge>
																												</td>
																												<td className="text-end">
																													<small className="text-muted">
																														{docsPorDia}
																													</small>
																												</td>
																											</tr>
																										);
																									}
																								)}
																							</tbody>
																						</Table>
																					</div>
																				</CardBody>
																			</Card>
																		</Col>
																	</Row>

																	{/* Análisis Adicional */}
																	<Row className="mb-4">
																		<Col lg="6" md="12">
																			<Card className="shadow-sm">
																				<CardHeader className="bg-light d-flex justify-content-between align-items-center border-0">
																					<h6 className="mb-0 text-dark">
																						<i className="fas fa-trophy me-2"></i>
																						Top 10 Sucursales por Facturación
																					</h6>
																					<Button
																						color="success"
																						size="sm"
																						onClick={downloadTopSucursalesExcel}
																						className="d-flex align-items-center">
																						<i className="fas fa-download me-1"></i>
																						Excel
																					</Button>
																				</CardHeader>
																				<CardBody className="p-3">
																					<div className="table-responsive">
																						<Table size="sm" className="mb-0">
																							<thead>
																								<tr>
																									<th style={{ width: "30px" }}>
																										#
																									</th>
																									<th>Sucursal</th>
																									<th className="text-end">
																										Importe
																									</th>
																									<th className="text-center">%</th>
																								</tr>
																							</thead>
																							<tbody>
																								{(sucursalesData?.data || [])
																									.slice(0, 10)
																									.map((sucursal, index) => (
																										<tr key={index}>
																											<td>
																												<Badge
																													color={
																														index < 3
																															? "success"
																															: "secondary"
																													}
																													pill>
																													{index + 1}
																												</Badge>
																											</td>
																											<td>
																												<strong>
																													{sucursal.sucursal}
																												</strong>
																											</td>
																											<td className="text-end">
																												<small>
																													{formatoCOP.format(
																														sucursal.importe
																													)}
																												</small>
																											</td>
																											<td className="text-center">
																												<small className="text-muted">
																													{(
																														(sucursal.importe /
																															sucursalesData.total_importe) *
																														100
																													).toFixed(1)}
																													%
																												</small>
																											</td>
																										</tr>
																									))}
																							</tbody>
																						</Table>
																					</div>
																				</CardBody>
																			</Card>
																		</Col>
																		<Col lg="6" md="12">
																			<Card className="shadow-sm">
																				<CardHeader className="bg-light border-0">
																					<h6 className="mb-0 text-dark">
																						<i className="fas fa-chart-bar me-2"></i>
																						Estadísticas del Sistema
																					</h6>
																				</CardHeader>
																				<CardBody className="p-3">
																					<div className="row g-3">
																						<div className="col-12">
																							<div className="d-flex justify-content-between border-bottom pb-2">
																								<span className="text-muted">
																									Sucursal con Mayor Facturación
																								</span>
																								<span className="fw-bold text-success">
																									{sucursalesData?.data?.[0]
																										?.sucursal || "N/A"}
																								</span>
																							</div>
																						</div>
																						<div className="col-12">
																							<div className="d-flex justify-content-between border-bottom pb-2">
																								<span className="text-muted">
																									Importe Máximo
																								</span>
																								<span className="fw-bold">
																									{formatoCOP.format(
																										sucursalesData?.data?.[0]
																											?.importe || 0
																									)}
																								</span>
																							</div>
																						</div>
																						<div className="col-12">
																							<div className="d-flex justify-content-between border-bottom pb-2">
																								<span className="text-muted">
																									Promedio por Sucursal
																								</span>
																								<span className="fw-bold text-info">
																									{formatoCOP.format(
																										sucursalesData.total_importe /
																										sucursalesData.total_sucursales
																									)}
																								</span>
																							</div>
																						</div>
																						<div className="col-12">
																							<div className="d-flex justify-content-between border-bottom pb-2">
																								<span className="text-muted">
																									Documentos Promedio
																								</span>
																								<span className="fw-bold text-warning">
																									{formatoNumero.format(
																										Math.round(
																											sucursalesData.total_documentos /
																											sucursalesData.total_sucursales
																										)
																									)}
																								</span>
																							</div>
																						</div>
																						<div className="col-12">
																							<div className="d-flex justify-content-between bg-light p-2 rounded">
																								<span className="fw-bold">
																									Concentración Top 5
																								</span>
																								<span className="fw-bold text-primary">
																									{sucursalesData?.data && sucursalesData.data.length > 0 ? (
																										(sucursalesData.data
																											.slice(0, 5)
																											.reduce(
																												(sum, s) => sum + s.importe,
																												0
																											) /
																											sucursalesData.total_importe) *
																										100
																									).toFixed(1) : '0'}
																									%
																								</span>
																							</div>
																						</div>
																					</div>
																				</CardBody>
																			</Card>
																		</Col>
																	</Row>
																</CardBody>
															</Card>
														</Col>
													</Row>
												</>
											)}

											{!sucursalesData && (
												<Row>
													<Col xl="12">
														<Alert color="info">
															<i className="fas fa-info-circle me-2"></i>
															Cargando datos de sucursales...
														</Alert>
													</Col>
												</Row>
											)}
										</>
									)}
								</TabPane>

								{/* Tab 3: Comparativo Diario */}
								<TabPane tabId="3" className="p-4">
									{isLoadingComparativo ? (
										<div className="text-center py-5">
											<Spinner color="warning" style={{ width: '3rem', height: '3rem' }} />
											<div className="mt-3">
												<h5 className="text-muted">Cargando Comparativo Diario...</h5>
												<p className="text-muted mb-0">Procesando análisis temporal</p>
											</div>
										</div>
									) : (
										<>
											{comparativoDiarioData && (
												<>
													{/* Resumen del Comparativo Diario */}
													<Row className="mb-4">
														<Col xl="12">
															<Card className="shadow-sm">
																<CardHeader className="bg-light d-flex justify-content-between align-items-center border-0">
																	<div>
																		<h6 className="mb-0 text-dark">
																			<i className="fas fa-calendar-alt me-2"></i>
																			Comparativo Diario -{" "}
																			{comparativoDiarioData.mes_actual} vs{" "}
																			{comparativoDiarioData.mes_anterior}
																		</h6>
																		<small className="text-muted">
																			{comparativoDiarioData.message} - Días
																			transcurridos:{" "}
																			{comparativoDiarioData.dias_transcurridos}
																		</small>
																	</div>
																	<Button
																		color="success"
																		size="sm"
																		onClick={downloadComparativoDiarioExcel}
																		className="d-flex align-items-center">
																		<i className="fas fa-download me-1"></i>
																		Excel
																	</Button>
																</CardHeader>
																<CardBody className="p-3">
																	{/* Resumen del Comparativo */}
																	<Row className="mb-4">
																		<Col lg="3" md="6" className="mb-3">
																			<div className="border rounded p-3 text-center bg-light">
																				<div
																					className="text-muted text-uppercase mb-1"
																					style={{ fontSize: "0.7rem" }}>
																					<strong>
																						{comparativoDiarioData.mes_actual}{" "}
																						{comparativoDiarioData.year_actual}
																					</strong>
																				</div>
																				<div className="h4 mb-0 font-weight-bold text-primary">
																					{formatoCOP.format(
																						comparativoDiarioData.total_actual
																					)}
																				</div>
																				<small className="text-muted">
																					facturación actual
																				</small>
																			</div>
																		</Col>
																		<Col lg="3" md="6" className="mb-3">
																			<div className="border rounded p-3 text-center bg-light">
																				<div
																					className="text-muted text-uppercase mb-1"
																					style={{ fontSize: "0.7rem" }}>
																					<strong>
																						{comparativoDiarioData.mes_anterior}{" "}
																						{comparativoDiarioData.year_anterior}
																					</strong>
																				</div>
																				<div className="h4 mb-0 font-weight-bold text-secondary">
																					{formatoCOP.format(
																						comparativoDiarioData.total_anterior
																					)}
																				</div>
																				<small className="text-muted">
																					facturación anterior
																				</small>
																			</div>
																		</Col>
																		<Col lg="3" md="6" className="mb-3">
																			<div className="border rounded p-3 text-center bg-light">
																				<div
																					className="text-muted text-uppercase mb-1"
																					style={{ fontSize: "0.7rem" }}>
																					<strong>Variación Total</strong>
																				</div>
																				<div
																					className={`h4 mb-0 font-weight-bold text-${variacionToColor(comparativoDiarioData.variacion_total)}`}>
																					{variacionToText(comparativoDiarioData.variacion_total)}
																				</div>
																				<small className="text-muted">
																					vs mes anterior
																				</small>
																			</div>
																		</Col>
																		<Col lg="3" md="6" className="mb-3">
																			<div className="border rounded p-3 text-center bg-light">
																				<div
																					className="text-muted text-uppercase mb-1"
																					style={{ fontSize: "0.7rem" }}>
																					<strong>Días Transcurridos</strong>
																				</div>
																				<div className="h4 mb-0 font-weight-bold text-info">
																					{comparativoDiarioData.dias_transcurridos}
																				</div>
																				<small className="text-muted">
																					días del mes
																				</small>
																			</div>
																		</Col>
																	</Row>

																	{/* Tabla Comparativa Diaria */}
																	<Row className="mb-4">
																		<Col xl="12">
																			<Card className="shadow-sm">
																				<CardHeader className="bg-light d-flex justify-content-between align-items-center border-0">
																					<h6 className="mb-0 text-dark">
																						<i className="fas fa-chart-line me-2"></i>
																						Comparativo Día a Día
																					</h6>
																				</CardHeader>
																				<CardBody className="p-0">
																					<div className="table-responsive">
																						<Table hover className="mb-0">
																							<thead className="table-light">
																								<tr>
																									<th>Día</th>
																									<th className="text-end">
																										{
																											comparativoDiarioData.mes_actual
																										}{" "}
																										{
																											comparativoDiarioData.year_actual
																										}
																									</th>
																									<th className="text-end">
																										{
																											comparativoDiarioData.mes_anterior
																										}{" "}
																										{
																											comparativoDiarioData.year_anterior
																										}
																									</th>
																									<th className="text-center">
																										Variación
																									</th>
																									<th className="text-center">
																										Tendencia
																									</th>
																								</tr>
																							</thead>
																							<tbody>
																								{(
																									comparativoDiarioData?.data || []
																								).map((dia, index) => (
																									<tr key={index}>
																										<td>
																											<strong>Día {dia.dia}</strong>
																										</td>
																										<td className="text-end">
																											<strong className="text-primary">
																												{formatoCOP.format(
																													dia.facturacion_actual
																												)}
																											</strong>
																										</td>
																										<td className="text-end">
																											<span className="text-muted">
																												{formatoCOP.format(
																													dia.facturacion_anterior
																												)}
																											</span>
																										</td>
																										<td className="text-center">
																											<Badge
																												color={variacionToColor(dia.variacion)}
																												pill>
																												{variacionToText(dia.variacion)}
																											</Badge>
																										</td>
																										<td className="text-center">
																											<i
																												className={`fas ${variacionToColor(dia.variacion) === "danger"
																													? "fa-arrow-down text-danger"
																													: "fa-arrow-up text-success"
																													}`}></i>
																										</td>
																									</tr>
																								))}
																							</tbody>
																						</Table>
																					</div>
																				</CardBody>
																			</Card>
																		</Col>
																	</Row>

																	{/* Gráfico de Tendencias Comparativas */}
																	<Row className="mb-4">
																		<Col xl="12">
																			<Card className="shadow-sm">
																				<CardHeader className="bg-light border-0">
																					<h6 className="mb-0 text-dark">
																						<i className="fas fa-chart-area me-2"></i>
																						Tendencia Comparativa Diaria
																					</h6>
																				</CardHeader>
																				<CardBody>
																					{getComparativoDiarioChartData() && (
																						<div
																							style={{
																								width: "100%",
																								height: "300px",
																							}}>
																							<Line
																								data={getComparativoDiarioChartData()}
																								options={{
																									responsive: true,
																									maintainAspectRatio: false,
																									plugins: {
																										legend: { position: "top" },
																										title: { display: false },
																									},
																									scales: {
																										y: {
																											beginAtZero: true,
																											title: {
																												display: true,
																												text: "Facturación",
																											},
																										},
																									},
																								}}
																							/>
																						</div>
																					)}
																				</CardBody>
																			</Card>
																		</Col>
																	</Row>
																</CardBody>
															</Card>
														</Col>
													</Row>
												</>
											)}

											{!comparativoDiarioData && (
												<Row>
													<Col xl="12">
														<Alert color="info">
															<i className="fas fa-info-circle me-2"></i>
															Cargando datos del comparativo diario...
														</Alert>
													</Col>
												</Row>
											)}
										</>
									)}
								</TabPane>

								{/* Tab 4: Documentos Generados */}
								<TabPane tabId="4" className="p-4">
									{isLoadingDocumentosGenerados ? (
										<div className="text-center py-5">
											<Spinner color="primary" style={{ width: '3rem', height: '3rem' }} />
											<div className="mt-3">
												<h5 className="text-muted">Cargando Documentos Generados...</h5>
												<p className="text-muted mb-0">Procesando información documental</p>
											</div>
										</div>
									) : (
										<>
											{documentosGeneradosData && (
												<>
													{/* Selector de Mes */}
													<Row className="mb-4">
														<Col xl="12">
															<Card className="shadow-sm">
																<CardHeader className="bg-light border-0">
																	<div className="d-flex justify-content-between align-items-center">
																		<h6 className="mb-0 text-dark">
																			<i className="fas fa-calendar-alt me-2"></i>
																			Seleccionar Mes
																		</h6>
																	</div>
																</CardHeader>
																<CardBody>
																	<Row>
																		<Col md="6">
																			<Label>Mes</Label>
																			<Input
																				type="select"
																				value={selectedMonth}
																				onChange={(e) => setSelectedMonth(parseInt(e.target.value))}
																				className="form-control"
																			>
																				<option value="1">Enero</option>
																				<option value="2">Febrero</option>
																				<option value="3">Marzo</option>
																				<option value="4">Abril</option>
																				<option value="5">Mayo</option>
																				<option value="6">Junio</option>
																				<option value="7">Julio</option>
																				<option value="8">Agosto</option>
																				<option value="9">Septiembre</option>
																				<option value="10">Octubre</option>
																				<option value="11">Noviembre</option>
																				<option value="12">Diciembre</option>
																			</Input>
																		</Col>
																		<Col md="6">
																			<Label>Año</Label>
																			<Input
																				type="number"
																				value={selectedYear}
																				onChange={(e) => setSelectedYear(parseInt(e.target.value))}
																				className="form-control"
																				min="2020"
																				max="2030"
																			/>
																		</Col>
																	</Row>
																</CardBody>
															</Card>
														</Col>
													</Row>

													{/* Tabla de Documentos Generados */}
													<Row>
														<Col xl="12">
															<Card className="shadow-sm border-primary">
																<CardHeader className="bg-primary text-white border-0">
																	<div className="d-flex justify-content-between align-items-center">
																		<div className="flex-grow-1 text-center">
																			<h5 className="mb-1 text-white">
																				<i className="fas fa-file-invoice me-2"></i>
																				DOCUMENTOS GENERADOS
																			</h5>
																			<p className="mb-0">
																				{documentosGeneradosData.nombre_emisor || 'Empresa'}
																				{' '}({documentosGeneradosData.nitemisor || ''})
																			</p>
																		</div>
																		<Button
																			color="light"
																			size="sm"
																			onClick={downloadDocumentosGeneradosExcel}
																			className="ms-2"
																		>
																			<i className="fas fa-file-excel me-1"></i>
																			Exportar Excel
																		</Button>
																	</div>
																</CardHeader>
																<CardBody className="p-0">
																	<div className="table-responsive">
																		<table className="table table-bordered table-hover mb-0">
																			<thead className="table-light">
																				<tr>
																					<th className="text-start ps-4" style={{ width: '35%' }}>
																						TIPO DE DOCUMENTO
																					</th>
																					<th className="text-center" colSpan="2" style={{ borderBottom: '2px solid #dee2e6' }}>
																						{documentosGeneradosData.periodo_mes?.toUpperCase() || ''}
																					</th>
																					<th className="text-center" colSpan="2" style={{ borderBottom: '2px solid #dee2e6' }}>
																						ACUMULADO
																					</th>
																				</tr>
																				<tr>
																					<th></th>
																					<th className="text-center" style={{ width: '15%' }}>CANT.</th>
																					<th className="text-center" style={{ width: '17.5%' }}>VALOR</th>
																					<th className="text-center" style={{ width: '15%' }}>CANT.</th>
																					<th className="text-center" style={{ width: '17.5%' }}>VALOR</th>
																				</tr>
																			</thead>
																			<tbody>
																				{documentosGeneradosData.tipos_documento?.map((doc, index) => (
																					<tr key={index} className={doc.tipo_documento?.includes('Nota de crédito') ? 'table-warning' : ''}>
																						<td className="ps-4">
																							<i className={`fas ${doc.tipo_documento?.includes('Factura') ? 'fa-file-invoice' :
																								doc.tipo_documento?.includes('crédito') ? 'fa-receipt' :
																									doc.tipo_documento?.includes('débito') ? 'fa-file-alt' :
																										'fa-file'
																								} me-2`}></i>
																							{doc.tipo_documento || 'Sin nombre'}
																						</td>
																						<td className="text-end pe-3">
																							{formatoNumero.format(doc.periodo?.cantidad || 0)}
																						</td>
																						<td className="text-end pe-3" style={{
																							color: doc.tipo_documento?.includes('Nota de crédito') ? '#d9534f' : 'inherit',
																							fontWeight: doc.tipo_documento?.includes('Nota de crédito') ? 'bold' : 'normal'
																						}}>
																							{doc.tipo_documento?.includes('Nota de crédito') && doc.periodo?.valor > 0 ? '-' : ''}
																							{formatoCOP.format(Math.abs(doc.periodo?.valor || 0))}
																						</td>
																						<td className="text-end pe-3">
																							{formatoNumero.format(doc.acumulado?.cantidad || 0)}
																						</td>
																						<td className="text-end pe-3" style={{
																							color: doc.tipo_documento?.includes('Nota de crédito') ? '#d9534f' : 'inherit',
																							fontWeight: doc.tipo_documento?.includes('Nota de crédito') ? 'bold' : 'normal'
																						}}>
																							{doc.tipo_documento?.includes('Nota de crédito') && doc.acumulado?.valor > 0 ? '-' : ''}
																							{formatoCOP.format(Math.abs(doc.acumulado?.valor || 0))}
																						</td>
																					</tr>
																				))}
																				{/* Fila de Totales */}
																				<tr className="table-dark fw-bold">
																					<td className="ps-4">
																						<i className="fas fa-calculator me-2"></i>
																						TOTALES
																					</td>
																					<td className="text-end pe-3">
																						{formatoNumero.format(documentosGeneradosData.totales?.periodo?.cantidad || 0)}
																					</td>
																					<td className="text-end pe-3">
																						{formatoCOP.format(documentosGeneradosData.totales?.periodo?.valor || 0)}
																					</td>
																					<td className="text-end pe-3">
																						{formatoNumero.format(documentosGeneradosData.totales?.acumulado?.cantidad || 0)}
																					</td>
																					<td className="text-end pe-3">
																						{formatoCOP.format(documentosGeneradosData.totales?.acumulado?.valor || 0)}
																					</td>
																				</tr>
																			</tbody>
																		</table>
																	</div>

																	{/* Notas informativas */}
																	<div className="p-3 bg-light border-top">
																		<small className="text-muted">
																			<i className="fas fa-info-circle me-1"></i>
																			<strong>Notas:</strong>
																		</small>
																		<ul className="mb-0 mt-2 small text-muted">
																			<li>Las notas de crédito se restan automáticamente en los totales</li>
																			<li>El acumulado corresponde al período completo del año {selectedYear}</li>
																			<li>Todos los tipos de documento están incluidos (incluso con valor cero)</li>
																		</ul>
																	</div>
																</CardBody>
															</Card>
														</Col>
													</Row>

													{/* Tabla de Indicadores Generales */}
													{indicadoresGeneralesData && (
														<Row className="mt-4">
															<Col xl="12">
																<Card>
																	<CardHeader className="bg-primary text-white">
																		<div className="d-flex justify-content-between align-items-center">
																			<h5 className="mb-0">
																				<i className="fas fa-chart-line me-2"></i>
																				INDICADORES GENERALES - DOCUMENTOS GENERADOS
																			</h5>
																			<Button
																				color="light"
																				size="sm"
																				onClick={downloadIndicadoresGeneralesExcel}
																				disabled={isLoadingIndicadores}
																			>
																				<i className="fas fa-download me-1"></i>
																				Exportar Excel
																			</Button>
																		</div>
																		<div className="mt-2 text-center">
																			<strong>{indicadoresGeneralesData.nombre_emisor || 'Empresa'}</strong>
																			{' '}({indicadoresGeneralesData.nitemisor || ''})
																		</div>
																		<div className="text-center">
																			<strong>AÑO {selectedYear}</strong>
																		</div>
																	</CardHeader>
																	<CardBody>
																		<div className="table-responsive">
																			<table className="table table-bordered table-hover mb-0">
																				<thead className="table-light">
																					<tr>
																						<th className="ps-4" style={{ width: '40%' }}>INDICADORES GENERALES</th>
																						{indicadoresGeneralesData.indicadores?.map((mes, index) => (
																							<th key={index} className="text-center" style={{ width: '20%' }}>
																								{mes.nombre_mes?.toUpperCase() || ''}
																							</th>
																						))}
																					</tr>
																				</thead>
																				<tbody>
																					{/* Fila 1: Cantidad total de documentos */}
																					<tr>
																						<td className="ps-4">
																							<i className="fas fa-file-alt me-2 text-primary"></i>
																							<strong>Cantidad total de documentos generados (1)</strong>
																						</td>
																						{indicadoresGeneralesData.indicadores?.map((mes, index) => (
																							<td key={index} className="text-end pe-3">
																								{formatoNumero.format(mes.cantidad_total_documentos || 0)}
																							</td>
																						))}
																					</tr>

																					{/* Fila 2: Número promedio diario */}
																					<tr className="table-light">
																						<td className="ps-4">
																							<i className="fas fa-calendar-day me-2 text-info"></i>
																							<strong>Número promedio diario de documentos (2)</strong>
																						</td>
																						{indicadoresGeneralesData.indicadores?.map((mes, index) => (
																							<td key={index} className="text-end pe-3">
																								{formatoNumero.format(mes.numero_promedio_diario || 0)}
																							</td>
																						))}
																					</tr>

																					{/* Fila 3: Valor total de operaciones */}
																					<tr>
																						<td className="ps-4">
																							<i className="fas fa-dollar-sign me-2 text-success"></i>
																							<strong>Valor total de las operaciones realizadas (3)</strong>
																						</td>
																						{indicadoresGeneralesData.indicadores?.map((mes, index) => (
																							<td key={index} className="text-end pe-3">
																								{formatoCOP.format(mes.valor_total_operaciones || 0)}
																							</td>
																						))}
																					</tr>

																					{/* Fila 4: Valor promedio diario */}
																					<tr className="table-light">
																						<td className="ps-4">
																							<i className="fas fa-chart-line me-2 text-warning"></i>
																							<strong>Valor promedio diario de las operaciones (2)(3)</strong>
																						</td>
																						{indicadoresGeneralesData.indicadores?.map((mes, index) => (
																							<td key={index} className="text-end pe-3">
																								{formatoCOP.format(mes.valor_promedio_diario_ops || 0)}
																							</td>
																						))}
																					</tr>
																				</tbody>
																			</table>
																		</div>

																		{/* Notas de indicadores */}
																		<div className="p-3 bg-light border-top mt-3">
																			<small className="text-muted">
																				<i className="fas fa-info-circle me-1"></i>
																				<strong>Notas:</strong>
																			</small>
																			<ul className="mb-0 mt-2 small text-muted">
																				<li>(1) Incluye todos los documentos generados en el mes</li>
																				<li>(2) Calculado basado en días reales del mes</li>
																				<li>(3) Las notas de crédito se restan en el cálculo de valores</li>
																			</ul>
																		</div>
																	</CardBody>
																</Card>
															</Col>
														</Row>
													)}
												</>
											)}

											{!documentosGeneradosData && (
												<Row>
													<Col xl="12">
														<Alert color="info">
															<i className="fas fa-info-circle me-2"></i>
															Seleccione un mes y año para consultar los documentos generados...
														</Alert>
													</Col>
												</Row>
											)}
										</>
									)}
								</TabPane>

								{/* Tab 5: Análisis Facturadores */}
								<TabPane tabId="5" className="p-4">
									{/* Controles de consulta */}
									<Row className="mb-4">
										<Col xl="12">
											<Card className="shadow-sm border-0" style={{ background: "linear-gradient(135deg, #1a237e 0%, #283593 100%)" }}>
												<CardBody className="p-3">
													<div className="d-flex flex-wrap align-items-center gap-3">
														<div className="text-white">
															<i className="fas fa-users fa-lg me-2"></i>
															<strong>Análisis Facturadores</strong>
														</div>
														<div className="d-flex align-items-center gap-2">
															<Label className="mb-0 text-white-50" style={{ fontSize: "0.8rem" }}>Desde</Label>
															<Input
																type="date"
																bsSize="sm"
																value={facturasInicio}
																onChange={(e) => setFacturasInicio(e.target.value)}
																style={{ maxWidth: 150 }}
															/>
														</div>
														<div className="d-flex align-items-center gap-2">
															<Label className="mb-0 text-white-50" style={{ fontSize: "0.8rem" }}>Hasta</Label>
															<Input
																type="date"
																bsSize="sm"
																value={facturasFin}
																onChange={(e) => setFacturasFin(e.target.value)}
																style={{ maxWidth: 150 }}
															/>
														</div>
														<Button
															color="warning"
															size="sm"
															disabled={!empresaSeleccionada || !facturasInicio || !facturasFin || isLoadingFacturasPersonalizadas}
															onClick={fetchFacturasPersonalizadas}>
															{isLoadingFacturasPersonalizadas ? (
																<><Spinner size="sm" className="me-1" />Consultando...</>
															) : (
																<><i className="mdi mdi-magnify me-1"></i>Consultar</>
															)}
														</Button>
														{facturasPersonalizadasData && (
															<Button
																color="success"
																size="sm"
																onClick={handleExportFacturasExcel}>
																<i className="fas fa-file-excel me-1"></i>Exportar Excel
															</Button>
														)}
														<Button
															color="outline-light"
															size="sm"
															onClick={() => { setFacturasPersonalizadasData(null); setFacturasInicio(""); setFacturasFin(""); }}>
															<i className="mdi mdi-refresh me-1"></i>Limpiar
														</Button>
													</div>
												</CardBody>
											</Card>
										</Col>
									</Row>

									{/* Spinner */}
									{isLoadingFacturasPersonalizadas && (
										<div className="text-center py-5">
											<Spinner color="primary" style={{ width: "3rem", height: "3rem" }} />
											<div className="mt-3">
												<h5 className="text-muted">Cargando análisis...</h5>
												<p className="text-muted mb-0">Procesando datos de facturadores</p>
											</div>
										</div>
									)}

									{/* Sin datos */}
									{!isLoadingFacturasPersonalizadas && !facturasPersonalizadasData && (
										<Row>
											<Col xl="12">
												<Alert color="info" className="d-flex align-items-center">
													<i className="fas fa-info-circle me-2 fa-lg"></i>
													Seleccione el rango de fechas y haga clic en <strong className="mx-1">Consultar</strong> para ver el análisis de facturadores.
												</Alert>
											</Col>
										</Row>
									)}

									{/* Contenido principal */}
									{!isLoadingFacturasPersonalizadas && facturasPersonalizadasData && (
										<>
											{/* Encabezado empresa */}
											<Row className="mb-3">
												<Col xl="12">
													<div className="d-flex align-items-center justify-content-between flex-wrap gap-2">
														<div>
															<h5 className="mb-0 text-dark fw-bold">
																<i className="fas fa-building me-2 text-primary"></i>
																{facturasPersonalizadasData.nombre_emisor}
															</h5>
															<small className="text-muted">
																NIT: {facturasPersonalizadasData.nitemisor} &nbsp;|&nbsp; Período: {facturasPersonalizadasData.fecha_inicio} al {facturasPersonalizadasData.fecha_fin}
															</small>
														</div>
														<Badge color="primary" pill className="px-3 py-2" style={{ fontSize: "0.85rem" }}>
															{facturasPersonalizadasData.dias?.length || 0} día(s) analizado(s)
														</Badge>
													</div>
												</Col>
											</Row>

											{/* KPIs de volumen */}
											<Row className="mb-3">
												<Col lg="3" md="6" className="mb-3">
													<Card className="border-0 shadow-sm h-100" style={{ borderLeft: "4px solid #1a237e" }}>
														<CardBody className="py-3">
															<div className="d-flex justify-content-between align-items-start">
																<div>
																	<p className="text-muted mb-1" style={{ fontSize: "0.75rem", textTransform: "uppercase", fontWeight: 600 }}>Total Facturas</p>
																	<h3 className="mb-0 text-dark fw-bold">{formatoNumero.format(facturasPersonalizadasData.total_facturas || 0)}</h3>
																	<small className="text-muted">en el período</small>
																</div>
																<div className="rounded-circle d-flex align-items-center justify-content-center" style={{ width: 48, height: 48, background: "rgba(26,35,126,0.1)" }}>
																	<i className="fas fa-file-invoice fa-lg" style={{ color: "#1a237e" }}></i>
																</div>
															</div>
														</CardBody>
													</Card>
												</Col>
												<Col lg="3" md="6" className="mb-3">
													<Card className="border-0 shadow-sm h-100" style={{ borderLeft: "4px solid #1565c0" }}>
														<CardBody className="py-3">
															<div className="d-flex justify-content-between align-items-start">
																<div>
																	<p className="text-muted mb-1" style={{ fontSize: "0.75rem", textTransform: "uppercase", fontWeight: 600 }}>Personalizadas</p>
																	<h3 className="mb-0 fw-bold" style={{ color: "#1565c0" }}>{formatoNumero.format(facturasPersonalizadasData.total_personalizadas || 0)}</h3>
																	<small className="text-muted">con plantilla propia</small>
																</div>
																<div className="rounded-circle d-flex align-items-center justify-content-center" style={{ width: 48, height: 48, background: "rgba(21,101,192,0.1)" }}>
																	<i className="fas fa-star fa-lg" style={{ color: "#1565c0" }}></i>
																</div>
															</div>
															<div className="mt-2">
																<div className="progress" style={{ height: 4 }}>
																	<div className="progress-bar" role="progressbar"
																		style={{ width: `${Math.min(facturasPersonalizadasData.porcentaje_personalizadas || 0, 100)}%`, backgroundColor: "#1565c0" }}
																		aria-valuenow={facturasPersonalizadasData.porcentaje_personalizadas || 0}
																		aria-valuemin="0" aria-valuemax="100">
																	</div>
																</div>
																<small className="text-muted">{(facturasPersonalizadasData.porcentaje_personalizadas || 0).toFixed(2)}% del total</small>
															</div>
														</CardBody>
													</Card>
												</Col>
												<Col lg="3" md="6" className="mb-3">
													<Card className="border-0 shadow-sm h-100" style={{ borderLeft: "4px solid #546e7a" }}>
														<CardBody className="py-3">
															<div className="d-flex justify-content-between align-items-start">
																<div>
																	<p className="text-muted mb-1" style={{ fontSize: "0.75rem", textTransform: "uppercase", fontWeight: 600 }}>Genéricas</p>
																	<h3 className="mb-0 fw-bold" style={{ color: "#546e7a" }}>{formatoNumero.format(facturasPersonalizadasData.total_genericas || 0)}</h3>
																	<small className="text-muted">plantilla estándar</small>
																</div>
																<div className="rounded-circle d-flex align-items-center justify-content-center" style={{ width: 48, height: 48, background: "rgba(84,110,122,0.1)" }}>
																	<i className="fas fa-file fa-lg" style={{ color: "#546e7a" }}></i>
																</div>
															</div>
															<div className="mt-2">
																<div className="progress" style={{ height: 4 }}>
																	<div className="progress-bar" role="progressbar"
																		style={{ width: `${Math.min(100 - (facturasPersonalizadasData.porcentaje_personalizadas || 0), 100)}%`, backgroundColor: "#546e7a" }}
																		aria-valuenow={100 - (facturasPersonalizadasData.porcentaje_personalizadas || 0)}
																		aria-valuemin="0" aria-valuemax="100">
																	</div>
																</div>
																<small className="text-muted">{(100 - (facturasPersonalizadasData.porcentaje_personalizadas || 0)).toFixed(2)}% del total</small>
															</div>
														</CardBody>
													</Card>
												</Col>
												<Col lg="3" md="6" className="mb-3">
													<Card className="border-0 shadow-sm h-100" style={{ borderLeft: "4px solid #2e7d32" }}>
														<CardBody className="py-3">
															<div className="d-flex justify-content-between align-items-start">
																<div>
																	<p className="text-muted mb-1" style={{ fontSize: "0.75rem", textTransform: "uppercase", fontWeight: 600 }}>Valor Total Período</p>
																	<h4 className="mb-0 fw-bold" style={{ color: "#2e7d32" }}>{formatoCOP.format((facturasPersonalizadasData.total_personalizadas_valor || 0) + (facturasPersonalizadasData.total_genericas_valor || 0))}</h4>
																	<small className="text-muted">personalizadas + genéricas</small>
																</div>
																<div className="rounded-circle d-flex align-items-center justify-content-center" style={{ width: 48, height: 48, background: "rgba(46,125,50,0.1)" }}>
																	<i className="fas fa-dollar-sign fa-lg" style={{ color: "#2e7d32" }}></i>
																</div>
															</div>
														</CardBody>
													</Card>
												</Col>
											</Row>

											{/* KPIs de valor */}
											<Row className="mb-4">
												<Col lg="4" md="6" className="mb-3">
													<Card className="border-0 shadow-sm h-100" style={{ background: "linear-gradient(135deg, #1565c0, #1976d2)" }}>
														<CardBody className="text-white py-3">
															<p className="mb-1 opacity-75" style={{ fontSize: "0.75rem", textTransform: "uppercase", fontWeight: 600 }}>Valor Facturas Personalizadas</p>
															<h4 className="mb-2 fw-bold">{formatoCOP.format(facturasPersonalizadasData.total_personalizadas_valor || 0)}</h4>
															<div className="border-top border-white-50 pt-2 mt-1">
																<div className="d-flex justify-content-between">
																	<small className="opacity-75">Subtotal</small>
																	<small>{formatoCOP.format(facturasPersonalizadasData.subtotal_personalizadas || 0)}</small>
																</div>
																<div className="d-flex justify-content-between">
																	<small className="opacity-75">Impuestos</small>
																	<small>{formatoCOP.format(facturasPersonalizadasData.impuestos_personalizadas || 0)}</small>
																</div>
															</div>
														</CardBody>
													</Card>
												</Col>
												<Col lg="4" md="6" className="mb-3">
													<Card className="border-0 shadow-sm h-100" style={{ background: "linear-gradient(135deg, #37474f, #546e7a)" }}>
														<CardBody className="text-white py-3">
															<p className="mb-1 opacity-75" style={{ fontSize: "0.75rem", textTransform: "uppercase", fontWeight: 600 }}>Valor Facturas Genéricas</p>
															<h4 className="mb-2 fw-bold">{formatoCOP.format(facturasPersonalizadasData.total_genericas_valor || 0)}</h4>
															<div className="border-top border-white-50 pt-2 mt-1">
																<div className="d-flex justify-content-between">
																	<small className="opacity-75">Subtotal</small>
																	<small>{formatoCOP.format(facturasPersonalizadasData.subtotal_genericas || 0)}</small>
																</div>
																<div className="d-flex justify-content-between">
																	<small className="opacity-75">Impuestos</small>
																	<small>{formatoCOP.format(facturasPersonalizadasData.impuestos_genericas || 0)}</small>
																</div>
															</div>
														</CardBody>
													</Card>
												</Col>
												<Col lg="4" md="6" className="mb-3">
													<Card className="border-0 shadow-sm h-100" style={{ background: "linear-gradient(135deg, #1b5e20, #2e7d32)" }}>
														<CardBody className="text-white py-3">
															<p className="mb-1 opacity-75" style={{ fontSize: "0.75rem", textTransform: "uppercase", fontWeight: 600 }}>Facturación Total del Período</p>
															<h4 className="mb-2 fw-bold">{formatoCOP.format((facturasPersonalizadasData.total_personalizadas_valor || 0) + (facturasPersonalizadasData.total_genericas_valor || 0))}</h4>
															<div className="border-top border-white-50 pt-2 mt-1">
																<div className="d-flex justify-content-between">
																	<small className="opacity-75">Subtotal global</small>
																	<small>{formatoCOP.format((facturasPersonalizadasData.subtotal_personalizadas || 0) + (facturasPersonalizadasData.subtotal_genericas || 0))}</small>
																</div>
																<div className="d-flex justify-content-between">
																	<small className="opacity-75">Impuestos globales</small>
																	<small>{formatoCOP.format((facturasPersonalizadasData.impuestos_personalizadas || 0) + (facturasPersonalizadasData.impuestos_genericas || 0))}</small>
																</div>
															</div>
														</CardBody>
													</Card>
												</Col>
											</Row>

											{/* Tabla detalle por día */}
											{facturasPersonalizadasData.dias?.length > 0 && (
												<Row>
													<Col xl="12">
														<Card className="shadow-sm border-0">
															<CardHeader className="bg-white border-bottom d-flex justify-content-between align-items-center">
																<h6 className="mb-0 fw-bold text-dark">
																	<i className="fas fa-table me-2 text-primary"></i>
																	Detalle Diario
																</h6>
																<Badge color="secondary" pill>{facturasPersonalizadasData.dias.length} día(s)</Badge>
															</CardHeader>
															<CardBody className="p-0">
																<div className="table-responsive">
																	<Table className="mb-0 table-hover align-middle" size="sm">
																		<thead style={{ background: "#1a237e", color: "#fff" }}>
																			<tr>
																				<th className="ps-3">Fecha</th>
																				<th className="text-end">Total Fact.</th>
																				<th className="text-end">Personal.</th>
																				<th className="text-end">Genéricas</th>
																				<th className="text-end">% Personal.</th>
																				<th className="text-end">Subtotal Personal.</th>
																				<th className="text-end">IVA Personal.</th>
																				<th className="text-end">Total Personal.</th>
																				<th className="text-end">Subtotal Géner.</th>
																				<th className="text-end">IVA Géner.</th>
																				<th className="text-end pe-3">Total Géner.</th>
																			</tr>
																		</thead>
																		<tbody>
																			{facturasPersonalizadasData.dias.map((dia, idx) => (
																				<tr key={idx} style={{ borderBottom: "1px solid #f0f0f0" }}>
																					<td className="ps-3 fw-semibold">{dia.fecha}</td>
																					<td className="text-end">{formatoNumero.format(dia.total_facturas)}</td>
																					<td className="text-end text-primary fw-semibold">{formatoNumero.format(dia.total_personalizadas)}</td>
																					<td className="text-end text-secondary">{formatoNumero.format(dia.total_genericas)}</td>
																					<td className="text-end">
																						<Badge color={dia.porcentaje_personalizadas >= 5 ? "success" : dia.porcentaje_personalizadas >= 2 ? "warning" : "secondary"} pill>
																							{(dia.porcentaje_personalizadas || 0).toFixed(2)}%
																						</Badge>
																					</td>
																					<td className="text-end">{formatoCOP.format(dia.subtotal_personalizadas)}</td>
																					<td className="text-end">{formatoCOP.format(dia.impuestos_personalizadas)}</td>
																					<td className="text-end text-primary fw-semibold">{formatoCOP.format(dia.total_personalizadas_valor)}</td>
																					<td className="text-end">{formatoCOP.format(dia.subtotal_genericas)}</td>
																					<td className="text-end">{formatoCOP.format(dia.impuestos_genericas)}</td>
																					<td className="text-end pe-3 text-secondary fw-semibold">{formatoCOP.format(dia.total_genericas_valor)}</td>
																				</tr>
																			))}
																		</tbody>
																		<tfoot style={{ background: "#e8eaf6" }}>
																			<tr className="fw-bold">
																				<td className="ps-3" style={{ color: "#1a237e" }}>TOTALES</td>
																				<td className="text-end" style={{ color: "#1a237e" }}>{formatoNumero.format(facturasPersonalizadasData.total_facturas)}</td>
																				<td className="text-end text-primary">{formatoNumero.format(facturasPersonalizadasData.total_personalizadas)}</td>
																				<td className="text-end text-secondary">{formatoNumero.format(facturasPersonalizadasData.total_genericas)}</td>
																				<td className="text-end">
																					<Badge color="primary" pill>{(facturasPersonalizadasData.porcentaje_personalizadas || 0).toFixed(2)}%</Badge>
																				</td>
																				<td className="text-end">{formatoCOP.format(facturasPersonalizadasData.subtotal_personalizadas)}</td>
																				<td className="text-end">{formatoCOP.format(facturasPersonalizadasData.impuestos_personalizadas)}</td>
																				<td className="text-end text-primary">{formatoCOP.format(facturasPersonalizadasData.total_personalizadas_valor)}</td>
																				<td className="text-end">{formatoCOP.format(facturasPersonalizadasData.subtotal_genericas)}</td>
																				<td className="text-end">{formatoCOP.format(facturasPersonalizadasData.impuestos_genericas)}</td>
																				<td className="text-end pe-3 text-secondary">{formatoCOP.format(facturasPersonalizadasData.total_genericas_valor)}</td>
																			</tr>
																		</tfoot>
																	</Table>
																</div>
															</CardBody>
														</Card>
													</Col>
												</Row>
											)}
										</>
									)}
								</TabPane>
							</TabContent>
			</div>{/* end erp-tabs-wrapper */}
			</div>{/* end erp-dashboard */}
		</React.Fragment>
	);
};

const mapStateToProps = (state) => ({
	user: state.Login.user,
	empresas: state.Login.empresas,
	empresaSeleccionada: state.Login.empresaSeleccionada,
});

export default connect(mapStateToProps, { setBreadcrumbItems })(Dashboard);
